/**
 * tp-delivery-stepper.js — M2 L1 pre-checkout delivery capture (§3, §8.4, §8.5, §12).
 *
 * <tp-delivery-stepper> in the cart drawer footer and on /cart. Order (owner 2026-07-03):
 *   method → (delivery) postal → DATE (calendar) → TIME.
 * Dates depend only on method + FSA + product speed + cut-off; the time options then adapt
 * to the chosen date (today → evening only, no same-day midday; pickup → weekday/Sunday slots).
 * Writes 5 order-level cart attributes (§4), manages the midday fee line (§8.2), intercepts
 * checkout (CTA never grey — missing steps scroll+highlight), pushes GA4 events. Capacity (L2)
 * is an async App-Proxy stub that fails open. All DOM via createElement (no innerHTML).
 * Depends on window.TPDeliveryData. No ES modules (theme convention).
 */
(function () {
  if (customElements.get('tp-delivery-stepper')) return;

  var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var DOW_MIN = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  var MON = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  var DATE_HORIZON_DAYS = 180; // advance delivery + pickup bookable up to 180 days out (owner 2026-07-12; matches PDP copy + live PickEasy app)
  var L2_PROXY_ENABLED = false;
  // Discount safety buffer for the base-delivery compensation (§8.1). A checkout discount code
  // (e.g. the site-wide 8% popup) is applied AFTER the drawer, lowering the subtotal that native
  // Local Delivery evaluates for free-over-$85. To NEVER double-charge (native re-charges base +
  // our comp line), only add the comp when the cart clears $85 with room for this discount; in the
  // thin $85–$85/(1-buffer) band we skip it and accept the (harmless) under-collection instead.
  // ⚠️ Set to the store's MAX standing discount; larger one-off codes fall to the L3 refund monitor.
  var DISCOUNT_BUFFER = 0.08;
  var L2_PROXY_PATH = '/apps/tp-delivery/availability';

  // ── Door-to-door pricing (owner 2026-09-03) ─────────────────────────────────
  // The door-to-door line is an ordinary cart line, so it counts toward the subtotal Shopify's
  // Local Delivery uses for its free-over-threshold tier. Verified on live 2026-09-03: a $69.95
  // cake alone is quoted $5.59, the same cake + $9.95 door-to-door is quoted FREE. Flipping the
  // fee product's requiresShipping to false changes nothing — the threshold counts every line.
  // Net effect in that band: we collect $9.95 and pay the courier $7.99 (base) + $5.00 (door) =
  // −$3.04 per order, versus +$1.91 when the delivery fee is actually collected.
  // So in the band where the base fee WOULD have been charged but this line tips the cart over
  // the threshold, the service is priced at $12.95 instead, recovering the waived fee.
  //
  // ⚠️ The band MUST be derived from the BASE price ($9.95), never from the raised one. Judging by
  // $12.95 would widen the band from [freeAt−9.95, freeAt) to [freeAt−12.95, freeAt) and drag in
  // orders that were never going to cross the threshold — those would lose the $4.95 fee to gain
  // $3.00, i.e. $1.95 WORSE per order. See the analysis in HANDOFF (2026-09-03).
  var DOOR_BASE_CENTS = 995;
  var DOOR_HIGH_CENTS = 1295;

  var ATTR = {
    method: 'tp_fulfillment_method', date: 'tp_delivery_date', window: 'tp_delivery_window',
    postal: 'tp_delivery_postal', surcharge: 'tp_delivery_surcharge',
  };

  // Cross-shop links for the speed-mismatch guidance (owner 2026-07-22). Since 2026-07-28 the
  // store DOES have a machine-readable Express↔Advance pairing (merch.*_counterpart, SSR'd per
  // cart line as `cp` — see the snippet); the swap flow below uses it. These collection links
  // remain the fallback for unpaired products and as the browse-elsewhere escape hatch.
  var COLL_EXPRESS = '/collections/express-cake';
  var COLL_ADVANCE = '/collections/all-cakes';

  // $30 standard delivery minimum (owner 2026-09-24). Below it the core GTA zone is charged the
  // full delivery rate (no reduced / free tier) and the outer zones (Brampton + the three fixed-day
  // zones) get no Local Delivery at all — both verified on live 2026-09-24 via Storefront
  // cartCreate ($16.95: core $12.42, outer = no option; $33.90: $5.59 / 7.85 / 13.56 / 11.30 / 16.95).
  // All amounts come from TPDeliveryData.FEE_ZONES — never hardcode them here. Every full-size cake
  // (6" from $50.95) clears $30 on its own; only mini cakes / cake boxes land under it, so the
  // "add a little more" link points at that collection.
  var COLL_MINI = '/collections/mini-cakes';

  // Premium-service copy (owner 2026-07-08). Shown on demand via the ⓘ toggles — paid options
  // must explain themselves: standard service is included, the surcharge buys something specific.
  var COPY = {
    middayInfo: 'Evening delivery travels on our shared route and is included in the standard delivery fee. The priority afternoon window sends a dedicated earlier trip just for your area — off the shared run — so your cake arrives hours sooner. The small fee covers that private trip.',
    doorTitle: 'Door-to-door · to your unit',
    doorSub: 'Condo & apartment buildings',
    doorInfo: 'Standard delivery is to your building’s front entrance — meeting the driver there keeps every cake fresh and on time. Add door-to-door and the driver will bring your order right up to your unit (covers condo / apartment building access). Available in all delivery areas.',
  };

  // ── safe DOM builder ──────────────────────────────────────────────────────────
  function h(tag, attrs, children) {
    var e = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) { if (k === 'class') e.className = attrs[k]; else if (attrs[k] != null) e.setAttribute(k, attrs[k]); });
    (children || []).forEach(function (c) { if (c == null) return; e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return e;
  }
  function strong(t) { return h('strong', null, [t]); }

  // 无蛋 × Express(店主 2026-10-05):Express 版都不能选无蛋(后台 28 对双胞胎实查,Express 侧
  // cake.eggless_available 全为 false),但换轨会把 Eggless=Yes 和 $5 无蛋费行原样带过去
  // (TPS7063TO)。店主定:照样让换、照样结账,只弹说明 —— 无蛋通常要提前 2 天,
  // 请先结账,再带订单号打电话确认。三处出现:换轨面板 / 混车「全部改 Express」/ 换完后常驻。
  function isEggless(l) { return !!(l && l.props && l.props.Eggless === 'Yes'); }
  // 尺寸 × Express(店主 2026-10-09,TPS7118TO):同一思路。Express 生酮/0糖款 Shopify 上有可售
  // 10 寸变体,不卖只靠 merch.version_size_limits(配置器藏按钮);换轨精确同变体直换 → 10 寸
  // 进了今明订单。店主定:照样让换、照样结账,只提示先结账再来电确认。
  // 规则判断与配置器 ruleAllowsSize 同口径:版本名小写 contains 匹配 key;deny 精确比尺寸;
  // max_inch 只管带 inch 的尺寸。vsl 由 SSR 每行带(行本身 vsl、双胞胎 cp.vsl)。
  function sizeRuleDenies(vsl, sig, size) {
    if (!vsl || typeof vsl !== 'object' || !size) return false;
    var parts = sigParts(sig), ver = null, k;
    for (k in parts) if (/^version$/i.test(k)) ver = parts[k];
    if (!ver) return false;
    var vl = String(ver).toLowerCase(), r = null;
    Object.keys(vsl).forEach(function (key) { if (key && vl.indexOf(String(key).toLowerCase()) >= 0) r = vsl[key]; });
    if (!r) return false;
    if (r.deny && r.deny.indexOf(size) >= 0) return true;
    if (r.max_inch != null) { var inch = sizeInches(size); if (inch !== null && inch > Number(r.max_inch)) return true; }
    return false;
  }
  // 车里这行是 Express 且尺寸超规则 → 返回尺寸值,否则 null
  function overSizeExpress(l) { return (l && l.speed === 'express' && sizeRuleDenies(l.vsl, l.sig, l.size)) ? l.size : null; }
  // 换轨计划里将会超规则的尺寸(moves = 已定目标;chooses = 客人待挑的口味,任一选项超就提前说)
  function planOverSizes(plan, cpOf) {
    var out = [];
    var add = function (v) { if (v && out.indexOf(v) < 0) out.push(v); };
    plan.moves.forEach(function (m) { if (sizeRuleDenies(cpOf(m.line), m.to[3], m.to[4])) add(m.to[4]); });
    plan.chooses.forEach(function (c) { c.options.forEach(function (o) { if (sizeRuleDenies(cpOf(c.line), o.v[3], o.v[4])) add(o.v[4]); }); });
    return out;
  }
  // 「需提前 2 天的要求」统一说明:eggless / 若干尺寸,合成一句,别叠两条
  function shortNoticeNote(lead, eggless, sizes) {
    sizes = sizes || [];
    if (!eggless && !sizes.length) return null;
    var names = (eggless ? ['Eggless'] : []).concat(sizes);
    var subj = names.length === 1 ? names[0] : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
    var what = (eggless ? ['eggless'] : []).concat(sizes);
    var obj = what.length === 1 ? what[0] : what.slice(0, -1).join(', ') + ' and ' + what[what.length - 1];
    return h('p', { class: 'tp-cal__intent is-warn', 'data-tp-eggless-note': eggless ? '' : null, 'data-tp-size-note': sizes.length ? sizes.join(',') : null }, [
      lead, strong(subj + (names.length === 1 ? ' usually needs' : ' usually need') + ' at least 2 days’ notice.'),
      ' Please check out as normal, then call us at ', h('a', { href: 'tel:+18883569099' }, ['1-888-356-9099']),
      ' with your order number so we can confirm your ' + obj + ' cake.',
    ]);
  }
  function replace(p, nodes) { p.textContent = ''; nodes.forEach(function (n) { if (n != null) p.appendChild(typeof n === 'string' ? document.createTextNode(n) : n); }); }

  // ── utils ─────────────────────────────────────────────────────────────────────
  function routeFor(k, f) { var r = window.routes || {}; return r[k] || f; }
  function jsonFetch(url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }).then(function (r) { return r.json(); });
  }
  // Cart writes that MUST fail loudly (fee lines). jsonFetch resolves on 4xx too — Shopify answers
  // errors with a JSON body and a non-2xx status — so a failed /cart/change.js (stale line key →
  // 404) used to look like success, and the caller would happily add the replacement line on top of
  // the one it never removed: two identical fee lines in the cart. Same defect class as the swap
  // double-count incident (2026-08-26, 3 cakes at checkout); that path was fixed, this one wasn't.
  function cartWrite(url, body, signal) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body), signal: signal })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          if (!r.ok || (j && (j.status >= 400 || j.description))) {
            var e = new Error((j && (j.description || j.message)) || ('cart write failed: HTTP ' + r.status));
            e.cartStatus = r.status;
            throw e;
          }
          return j;
        });
      });
  }
  // 单次属性写入的上限(2026-09-29,TPS7012TO)。一个挂死的请求原来会让 persist 队列永远
  // _writing=true:之后客人再怎么选,新选择都只进 _pending、永远发不出去。到点就 abort →
  // 按「写失败」处理(_writeFailed 留痕,队列接着发最新的)。正常写入 0.3–0.6 秒,6 秒远超。
  var PERSIST_TIMEOUT_MS = 6000;
  // 结账前最后一次读服务器的上限:读不到 = 网络问题 → 按 2026-09-09 口径放行,不因此拦客人
  var FINAL_READ_TIMEOUT_MS = 3000;
  function withTimeout(ms, run) {
    var ctl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = ctl ? setTimeout(function () { try { ctl.abort(); } catch (e) {} }, ms) : null;
    var done = function () { if (timer) { clearTimeout(timer); timer = null; } };
    return run(ctl ? ctl.signal : undefined).then(function (v) { done(); return v; }, function (err) { done(); throw err; });
  }

  // ── 跨标签页同步(2026-09-29,TPS7012TO)──────────────────────────────────────────
  // 同一浏览器里两个页面共用一辆购物车。9-28 的修复只在「标签页回到前台」时重读,但两个窗口
  // 并排、或新页面在后台打开时,旧页面一直算「前台」,内存里的旧选择(有时段)就一直挂着,
  // 而服务器上的时段已被另一个页面清掉 → 在旧页面点结账 = 订单缺时段(7012 在 live 测试车复现)。
  // 做法:任何页面写入配送属性成功后广播一声,其他页面从 /cart.js 重读(只重读)。
  //   · 只读不写:不调 revalidate —— 它会顺带 reconcileFees,而发出广播的那个页面此刻可能正在
  //     加下午档/送上楼费用行,两边同时加 = 两条费用行(见 memory fee-line-reconcile-never-two)。
  //     费用行照旧由操作的那个页面负责;本页点结账时 flushForCheckout 还会再对一次。
  //   · 本页自己还有写入在飞/排队时跳过:那条写入更新,它落地后会再广播给别人。
  //   · 同页的抽屉 + /cart 两个实例仍走原有 tp:delivery-updated,这里不重复。
  //   · 没有 BroadcastChannel 的老浏览器 → 静默无此功能,其余行为不变。
  var XTAB = null;
  try { if (typeof BroadcastChannel === 'function') XTAB = new BroadcastChannel('tp-delivery-attrs'); } catch (e) { XTAB = null; }
  function xtabAnnounce() { try { if (XTAB) XTAB.postMessage({ t: 'attrs', at: Date.now() }); } catch (e) {} }
  if (XTAB) {
    var xtabTimer = null;
    XTAB.onmessage = function () {
      clearTimeout(xtabTimer);
      xtabTimer = setTimeout(function () {
        try {
          Array.prototype.forEach.call(document.querySelectorAll('tp-delivery-stepper'), function (st) {
            if (!st.isConnected || !st._init || typeof st.hydrateFromCart !== 'function') return;
            if (st._writing || st._pending) return;
            st.hydrateFromCart();
          });
        } catch (e) {}
      }, 300);
    };
  }
  // ── 原生结账配送偏好同步(2026-09-20)────────────────────────────────────────────
  // 要解决的问题:客人在购物车选了「自提」,Shopify Checkout 仍然默认「Ship」,还塞给他一张
  // 配送地址表(2026-09-20 在 live 站实测复现:属性写着 pickup,结账页 delivery_method-SHIPPING
  // 仍为选中态)。Basic 套餐改不了 checkout UI,唯一的办法是在离开购物车前,把客人的选择写进
  // 购物车原生的 buyerIdentity.preferences.delivery.deliveryMethod。
  //
  // 实测结论(2026-09-20,live 站匿名测试车,已清空未下单;证据见 memory
  // storefront-cart-gid-tokenless-same-origin):
  //   ① /cart.js 的 token(形如 `xxxx?key=<32hex>`)原样拼 `gid://shopify/Cart/<token>`,
  //      读回的 lines 与 Ajax 当前车逐项一致 = 同一辆车,不需要 cartCreate;
  //   ② 本店 Storefront API 同源免 token:POST /api/2026-07/graphql.json 不带
  //      X-Shopify-Storefront-Access-Token 也返回 200 → 主题里不放任何凭证,也没有 CORS;
  //   ③ 写入后 Checkout 默认项双向都跟着变,而且另一个选项仍然可选(没有剥夺客人改的能力);
  //   ④ 连写三次偏好,购物车 token / 行 key / 数量 / 价格 / line properties / 属性 / note
  //      逐字段比对完全一致 —— 这个 mutation 不碰购物车内容。
  //
  // 🔴 这一层永远只是锦上添花,绝不能成为结账的前提。四条硬约束:
  //   · tp_* 购物车属性仍是履约真相,本机制失效完全不影响出货判断;
  //   · 任何失败/超时一律 fail-open 放行(沿用店主 2026-09-09 决定),退回今天的行为;
  //   · 它有自己的预算和自己的埋点,**不改动 flushForCheckout 的 ok/reason 原有语义**;
  //   · window.TP_PREF_SYNC === 'off'(shop metafield merch.delivery_pref_sync)一键关停,
  //     不用推主题代码 —— 这是最外层兜底。
  var PREF_ENDPOINT = '/api/2026-07/graphql.json';
  var PREF_ENUM = { pickup: 'PICK_UP', delivery: 'SHIPPING' };
  var PREF_BUDGET_MS = 2500;
  var PREF_MUTATION =
    'mutation TPSyncDeliveryPreference($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {' +
    '  cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {' +
    '    cart { id buyerIdentity { preferences { delivery { deliveryMethod } } } }' +
    '    userErrors { field message code }' +
    '  }' +
    '}';

  // 总开关。metafield 不存在 → undefined → 开。只有显式的 "off" 才关停。
  function prefSyncEnabled() {
    try { return String(window.TP_PREF_SYNC == null ? '' : window.TP_PREF_SYNC).trim().toLowerCase() !== 'off'; }
    catch (err) { return true; }
  }

  // opaque token 原样用,连 `?key=` 一起 —— 不截断、不解码、不再编码。格式不认识就返回 null
  // 走受控失败,绝不猜、绝不拿另一辆车顶上。也处理已经带 gid:// 前缀的情况。
  function cartGidFrom(token) {
    var t = String(token == null ? '' : token);
    if (t.indexOf('gid://') === 0) t = t.slice(t.lastIndexOf('/') + 1);
    if (!/^[A-Za-z0-9_-]+(\?key=[A-Za-z0-9_-]+)?$/.test(t)) return null;
    return 'gid://shopify/Cart/' + t;
  }
  // 同车判定只看 `?key=` 之前的车号:key 是访问凭证,可能被服务端换发,车号不会变。
  function cartNumOf(gid) { return String(gid == null ? '' : gid).split('?')[0]; }

  // 成功 = HTTP 成功 且 无 GraphQL errors 且 无 userErrors 且 cart 非空 且 是同一辆车
  // 且 读回的 deliveryMethod 正好等于这次要写的那一个。HTTP 200 本身不算成功。
  function writeDeliveryPreference(gid, methodEnum, signal) {
    return fetch(PREF_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      signal: signal,
      body: JSON.stringify({
        query: PREF_MUTATION,
        // 只传这一个枚举。绝不同时传两个,也绝不顺手带空的 email/phone/countryCode/address
        // 或 customerAccessToken —— 那会清掉购物车上已有的身份。
        variables: { cartId: gid, buyerIdentity: { preferences: { delivery: { deliveryMethod: [methodEnum] } } } }
      })
    }).then(function (r) {
      if (!r.ok) throw new Error('http_' + r.status);
      return r.json();
    }).then(function (j) {
      if (j && j.errors && j.errors.length) throw new Error('graphql_errors');
      var payload = j && j.data && j.data.cartBuyerIdentityUpdate;
      if (!payload) throw new Error('no_payload');
      if (payload.userErrors && payload.userErrors.length) throw new Error('user_errors');
      var cart = payload.cart;
      if (!cart || cartNumOf(cart.id) !== cartNumOf(gid)) throw new Error('cart_mismatch');
      var bi = cart.buyerIdentity && cart.buyerIdentity.preferences && cart.buyerIdentity.preferences.delivery;
      var got = bi && bi.deliveryMethod;
      if (!got || got.length !== 1 || got[0] !== methodEnum) throw new Error('readback_mismatch');
      return true;
    });
  }

  function money(cents) {
    if (typeof window.Shopify !== 'undefined' && Shopify.formatMoney && window.theme && theme.moneyFormat) return Shopify.formatMoney(cents, theme.moneyFormat);
    return '$' + (cents / 100).toFixed(2);
  }
  // whole-dollar threshold label ("$30") — order-value thresholds are never taxed or fractional
  function dollars(cents) { return '$' + Math.round(cents / 100); }
  // GTM was removed 2026-07-02; the storefront uses gtag (G-DHP3C19SXT) directly. Send events
  // via gtag so they actually reach GA4 — a bare dataLayer.push has no consumer without GTM and
  // is silently dropped. dataLayer.push stays only as a no-op-safe fallback.
  // C4: route through the shared base, which QUEUES until gtag exists. GA4 comes
  // from Shopify's async web pixel, so the old "if gtag exists, else dataLayer"
  // shape dropped anything fired early — tp_drawer_open (sent the instant the
  // drawer opens) was the most exposed. Falls back to the old behaviour if the
  // base script hasn't loaded. Contract: docs/specs/C4-event-contract-v1.md
  function track(event, params) {
    if (window.TPTrack && window.TPTrack.event) { window.TPTrack.event(event, params); return; }
    if (typeof window.gtag === 'function') { window.gtag('event', event, params || {}); return; }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, params || {}));
  }

  // ── C4 · fulfillment refusals ────────────────────────────────────────────────
  // The stepper already recorded what the customer CHOSE; it never recorded what
  // the system REFUSED. "Checkout blocked, and why" is the direct answer to where
  // add-to-cart → checkout leaks. Customer-facing wording carries concrete times
  // ("Same-day cut-off (2:30 PM) has passed") and owner-typed text, so it is
  // mapped to fixed codes here and never sent raw.
  var REFUSAL_CODES = [
    [/express items/i,                'express_window_only'],
    [/advance items/i,                'advance_lead_time'],
    [/not a delivery day/i,           'not_delivery_day'],
    [/no same-day service/i,          'no_sameday_sunday'],
    [/cut-off/i,                      'cutoff_passed'],
    [/close at .* saturday/i,         'sunday_closes_saturday'],
    [/no pickup times/i,              'no_pickup_times'],
    [/no delivery times/i,            'no_delivery_times']
  ];
  function refusalCode(reason) {
    if (!reason) return 'unspecified';
    for (var i = 0; i < REFUSAL_CODES.length; i++) {
      if (REFUSAL_CODES[i][0].test(reason)) return REFUSAL_CODES[i][1];
    }
    return 'slot_blocked';   // remaining source is the owner-typed capacity table
  }
  // Same date can be re-tested many times per render; only the first of each
  // (reason × method) pair is worth an event or the counts are meaningless.
  var refusalSeen = {};
  function trackRefusal(stage, code, method) {
    try {
      var k = stage + '|' + code + '|' + (method || 'none');
      // QA probe: lets a tester confirm the refusal path ran even when the event
      // itself is deduped or gtag hasn't loaded. Read-only, no behaviour.
      try { window.__tpLastRefusal = k; window.__tpRefusalCount = (window.__tpRefusalCount || 0) + 1; } catch (e) {}
      if (refusalSeen[k]) return;
      refusalSeen[k] = 1;
      track('tp_fulfillment_blocked', {
        funnel_stage: stage === 'checkout' ? 'checkout' : 'delivery',
        stage: stage, reason: code, method: method || 'none'
      });
    } catch (e) { /* analytics must never break checkout */ }
  }
  function parseYMD(s) { var p = (s || '').split('-'); return Date.UTC(+p[0], +p[1] - 1, +p[2]); }
  function ymOf(ms) { var d = new Date(ms); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1); }
  function addDays(ms, n) { return ms + n * 86400000; }
  function addMonths(ms, n) { var d = new Date(ms); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1); }
  function fmtYMD(ms) { var d = new Date(ms), m = d.getUTCMonth() + 1, day = d.getUTCDate(); return d.getUTCFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day); }
  function dowOf(ms) { return DOW[new Date(ms).getUTCDay()]; }
  function dowIdx(ms) { return new Date(ms).getUTCDay(); }
  function niceDay(iso) { var d = new Date(parseYMD(iso)); return DOW[d.getUTCDay()] + ', ' + MON[d.getUTCMonth()].slice(0, 3) + ' ' + d.getUTCDate(); }
  // 快赢1/2 换轨助手:签名 = "Name=Value|…"(SSR 与双胞胎两侧同构);尺寸兜底只认英寸
  // ("12 oz" 类盒装解析为 null → 只允许精确匹配,不做近似)。
  function sizeInches(s) { var m = /(\d+(?:\.\d+)?)\s*inch/i.exec(s || ''); return m ? parseFloat(m[1]) : null; }
  function sigParts(sig) { var o = {}; (sig || '').split('|').forEach(function (p) { var i = p.indexOf('='); if (i > 0) o[p.slice(0, i)] = p.slice(i + 1); }); return o; }
  function isSizeKey(k) { return /^size$/i.test(k); }
  function isFlavorKey(k) { return /^flavou?r$/i.test(k); }
  function cleanTitle(t) { return String(t || '').replace(/\*EXPRESS\*\s*/gi, '').trim(); }

  class TPDeliveryStepper extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.data = window.TPDeliveryData;
      this.syncing = Promise.resolve();
      this.serverDate = this.dataset.serverDate;
      this.serverTime = this.dataset.serverTime || '00:00';
      this._epoch = Date.now(); // §8.4 time base: SSR store-time + client elapsed, never the client clock
      this.context = this.dataset.context || 'drawer';

      this.state = {
        method: this.dataset.method || null,
        postal: this.dataset.postal || null, coverage: null,
        date: this.dataset.date || null,
        windowId: null, windowLabel: this.normWindow(this.dataset.window), windowKind: null,
        surcharge: this.dataset.surcharge || '0',
        door: false, // door-to-door premium service; source of truth = its fee line in the cart
      };
      if (this.state.postal) this.state.coverage = this.data.lookupFSA(this.state.postal);
      this.state.windowKind = this.inferWindowKind();
      this.calMonth = null; // calendar view month (ms of 1st)
      this._info = {}; // which ⓘ explanations are expanded
      var self0 = this;
      this.currentFeeLines().then(function (f) { if (f.door.length && !self0.state.door) { self0.state.door = true; self0.render(); } });

      this.cacheDom();
      this.bind();
      this.revalidate();
      track('tp_drawer_open', { tp_context: this.context });
    }

    cacheDom() {
      var q = this.querySelector.bind(this);
      this.els = {
        steps: q('[data-tp-steps]'), method: q('[data-step="method"]'), postal: q('[data-step="postal"]'),
        date: q('[data-step="date"]'), window: q('[data-step="window"]'),
        premium: q('[data-step="premium"]'), premiumBox: q('[data-tp-premium]'),
        postalInput: q('[data-tp-postal-input]'), postalResult: q('[data-tp-postal-result]'),
        dateBox: q('[data-tp-date-calendar]'), windowOptions: q('[data-tp-window-options]'),
        mixedNotice: q('[data-tp-mixed]'), guidance: q('[data-tp-guidance]'),
        minNotice: q('[data-tp-min-notice]'),
      };
    }

    // store-local "now" = SSR timestamp + elapsed wall time. A tab left open past the
    // cut-off (or overnight) must not keep offering "today" — the SSR time alone goes stale.
    nowParts() {
      var base = Date.parse(this.serverDate + 'T' + this.serverTime + ':00Z');
      var d = new Date(base + (Date.now() - this._epoch));
      var hh = d.getUTCHours(), mm = d.getUTCMinutes();
      return { date: fmtYMD(d.getTime()), time: (hh < 10 ? '0' : '') + hh + ':' + (mm < 10 ? '0' : '') + mm };
    }

    ssrLines() { var n = this.querySelector('[data-tp-cart-lines]'); if (!n) return []; try { return JSON.parse(n.textContent || '[]'); } catch (e) { return []; } }
    cartLines() { return this._lines || this.ssrLines(); }
    // The SSR line list goes stale when the cart changes without this snippet re-rendering
    // (fetch-based removals, the addon-guard cascade, /cart section morphs). Re-read the live
    // cart; keys we knew keep their SSR speed, lines added since SSR resolve their speed from
    // /products/{handle}.js tags (cached per handle). Falls back to the SSR list on any error.
    refreshLines() {
      var self = this;
      this._speedCache = this._speedCache || {};
      var ssrByKey = {};
      this.ssrLines().forEach(function (l) { ssrByKey[l.key] = l; });
      (this._lines || []).forEach(function (l) { ssrByKey[l.key] = l; });
      return fetch(routeFor('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          // keep the fee-excluded subtotal fresh (see cartSubtotalCents) — same rule as the SSR snippet
          self._liveSubtotal = (cart.items || []).reduce(function (n, it) {
            return (it.properties && it.properties._tp_fee) ? n : n + it.final_line_price;
          }, 0);
          var jobs = (cart.items || []).filter(function (it) { return !(it.properties && (it.properties._tp_fee || it.properties._tp_addon)); }).map(function (it) {
            var known = ssrByKey[it.key];
            // 快赢1/2:SSR 行携带的换轨数据(vid/sig/size/cp)原样带走,数量/属性以 live 为准
            if (known) return Promise.resolve(Object.assign({}, known, { key: it.key, title: it.product_title, q: it.quantity, props: it.properties || known.props || {} }));
            if (self._speedCache[it.handle] != null) return Promise.resolve({ key: it.key, title: it.product_title, speed: self._speedCache[it.handle], q: it.quantity, props: it.properties || {} });
            return fetch('/products/' + it.handle + '.js', { headers: { Accept: 'application/json' } })
              .then(function (r) { return r.json(); })
              .then(function (p) {
                var tags = p.tags || [];
                var speed = (tags.indexOf('*EXPRESS* Cake') !== -1 || tags.indexOf('Keto Express') !== -1) ? 'express' : 'advance';
                self._speedCache[it.handle] = speed;
                return { key: it.key, title: it.product_title, speed: speed, q: it.quantity, props: it.properties || {} };
              })
              .catch(function () { return { key: it.key, title: it.product_title, speed: 'advance', q: it.quantity, props: it.properties || {} }; });
          });
          return Promise.all(jobs).then(function (lines) { self._lines = lines; });
        })
        .catch(function () {});
    }
    // Cakes + add-ons subtotal in cents, every fee line excluded — same rule as the snippet's SSR
    // value (`unless item.properties._tp_fee`, summing final_line_price).
    // The SSR attribute is a SNAPSHOT: a long-lived stepper instance can outlive the markup that
    // produced it (AJAX morph), so refreshLines() keeps _liveSubtotal in sync from /cart.js and we
    // prefer it. A stale subtotal here would pick the wrong door-to-door tier.
    cartSubtotalCents() {
      if (typeof this._liveSubtotal === 'number') return this._liveSubtotal;
      var n = this.querySelector('[data-tp-cart-lines]'); var v = n && n.getAttribute('data-subtotal');
      return v ? parseInt(v, 10) : 0;
    }
    speedClass() {
      var lines = this.cartLines(); if (!lines.length) return 'empty';
      var e = false, a = false; lines.forEach(function (l) { if (l.speed === 'express') e = true; else a = true; });
      if (e && a) return 'mixed'; return e ? 'all_express' : 'all_advance';
    }

    bind() {
      var self = this;
      this.els.method && this.els.method.addEventListener('click', function (e) { var b = e.target.closest('[data-tp-method]'); if (b) self.selectMethod(b.dataset.tpMethod); });
      if (this.els.postalInput) {
        var run = function () { self.checkPostal(self.els.postalInput.value); };
        this.els.postalInput.addEventListener('change', run);
        this.els.postalInput.addEventListener('input', function () { if (self.data.normalizePostalCode(self.els.postalInput.value).length === 3) run(); });
      }
      this.els.dateBox && this.els.dateBox.addEventListener('click', function (e) {
        var nav = e.target.closest('[data-tp-cal-nav]'); if (nav) { self.calMonth = addMonths(self.calMonth, +nav.dataset.tpCalNav); self.renderDate(); return; }
        // 快赢1:换轨确认面板的按钮也在日历容器里,先于日期路由处理
        var sc = e.target.closest('[data-tp-swap-confirm]'); if (sc && !sc.disabled) { self.confirmSwap(sc.dataset.tpSwapConfirm); return; }
        var sx = e.target.closest('[data-tp-swap-cancel]'); if (sx) { self._swapFor = null; self.render(); return; } // 全量重画:时间段步随面板解锁
        var day = e.target.closest('[data-tp-day]'); if (day && !day.disabled) {
          if (day.dataset.tpSwap) { self.openSwapPanel(day.dataset.tpDay); return; }
          self.selectDate(day.dataset.tpDay);
        }
      });
      this.els.windowOptions && this.els.windowOptions.addEventListener('click', function (e) { var b = e.target.closest('[data-tp-window]'); if (b) self.selectWindow(b.dataset.tpWindow, b.dataset.tpWindowLabel, b.dataset.tpWindowKind, b.dataset.tpSurcharge); });
      this.els.premiumBox && this.els.premiumBox.addEventListener('click', function (e) { var b = e.target.closest('[data-tp-service]'); if (b) self.toggleService(b.dataset.tpService); });
      // ⓘ explanations for paid options (midday routing / door-to-door): toggle in place
      this.addEventListener('click', function (e) {
        var ib = e.target.closest('[data-tp-info]'); if (!ib) return;
        var k = ib.dataset.tpInfo;
        self._info[k] = !self._info[k];
        ib.setAttribute('aria-expanded', self._info[k] ? 'true' : 'false');
        var panel = ib.parentElement.querySelector('.tp-info-panel'); if (panel) panel.hidden = !self._info[k];
      });
      // mixed-cart one-click resolve (buttons re-created on every renderMixed → delegate)
      this.addEventListener('click', function (e) {
        var kb = e.target.closest ? e.target.closest('[data-tp-keep]') : null;
        if (kb && !kb.disabled) self.keepOnly(kb.dataset.tpKeep);
        // 快赢2:混车「换版本」按钮(与 keep 同容器,同样每次重建 → delegate)
        var cb = e.target.closest ? e.target.closest('[data-tp-convert]') : null;
        if (cb && !cb.disabled) self.confirmConvert(cb.dataset.tpConvert);
        // 口味选择 chip(换轨面板 + 混车通知共用;选完就近重画所在容器)
        var pk = e.target.closest ? e.target.closest('[data-tp-swap-pick]') : null;
        if (pk) {
          self._swapChoice = self._swapChoice || {};
          self._swapChoice[pk.dataset.tpSwapLine] = parseInt(pk.dataset.tpSwapPick, 10);
          if (pk.closest('[data-tp-mixed]')) self.renderMixed(); else self.renderDate();
        }
      });

      this.form = this.ownerForm();
      if (this.form && !this.form._tpBound) { this.form._tpBound = true; this.form.addEventListener('submit', function (e) { self.onCheckoutSubmit(e); }, true); }
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) this._unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, function () { self.revalidate(); });
      // also revalidate on the theme's DOM-level cart event (fires for changes the pub-sub misses,
      // e.g. the addon-guard cascade) — debounced, revalidate() re-fetches the cart
      this._onCartUpdated = function () { clearTimeout(self._cartUpdT); self._cartUpdT = setTimeout(function () { self.revalidate(); }, 400); };
      document.addEventListener('cart:updated', this._onCartUpdated);
      // sibling drawer/cart instance wrote → re-hydrate from the cart (ignore our own broadcast)
      this._onDelivUpdate = function (e) { if (!e.detail || e.detail.source !== self) self.hydrateFromCart(); };
      document.addEventListener('tp:delivery-updated', this._onDelivUpdate);
      // 标签页回到前台 / 从浏览器前进后退缓存恢复(2026-09-28,TPS6991TO 后补):内存里的选择可能
      // 早已过期 —— 客人在别的标签页改过、或已经去结账又回来。不先重读,这里随手一点(PDP 日历、
      // 邮编框)就会把旧选择(可能没有时段)整份写回服务器,盖掉新的。重读后再 revalidate:
      // 藏在后台期间可能已过截单,过期的日期/时段和对应费用行一并清掉。
      this._onResume = function (e) {
        if (e.type === 'pageshow' ? !e.persisted : document.visibilityState !== 'visible') return;
        self.hydrateFromCart().then(function () { self.revalidate(); });
      };
      document.addEventListener('visibilitychange', this._onResume);
      window.addEventListener('pageshow', this._onResume);
    }

    ownerForm() {
      var scope = this.closest('[data-purchase-conditions]') || document;
      var btn = scope.querySelector('button[name="checkout"], input[name="checkout"]');
      if (btn) { var fid = btn.getAttribute('form'); if (fid) { var f = document.getElementById(fid); if (f) return f; } var cf = btn.closest('form'); if (cf) return cf; }
      return this.closest('form');
    }

    // ── step actions ────────────────────────────────────────────────────────────
    selectMethod(m) {
      if (this.state.method === m) return;
      this.state.method = m;
      this.state.date = this.state.windowId = this.state.windowLabel = this.state.windowKind = null;
      this.state.surcharge = '0'; this.calMonth = null;
      if (m === 'pickup') { this.state.postal = null; this.state.coverage = null; this.state.door = false; }
      track('delivery_method_selected', { tp_method: m });
      this.persist(); this.reconcileFees(); this.render();
    }
    toggleService(kind) {
      if (kind !== 'door') return;
      this.state.door = !this.state.door;
      track('premium_service_toggled', { tp_service: 'door_to_door', tp_on: this.state.door ? 'yes' : 'no' });
      this.reconcileFees(); this.render();
    }
    checkPostal(raw) {
      var fsa = this.data.normalizePostalCode(raw);
      if (fsa.length < 3) { this.state.coverage = null; this.renderPostalResult(null); return; }
      var cov = this.data.lookupFSA(fsa), changed = !this.state.coverage || this.state.coverage.fsa !== fsa;
      this.state.postal = fsa; this.state.coverage = cov;
      if (changed) { this.state.date = this.state.windowId = this.state.windowLabel = this.state.windowKind = null; this.state.surcharge = '0'; this.calMonth = null; }
      track('postal_checked', { tp_postal: fsa, tp_result: cov.deliverable ? 'deliverable' : 'out_of_area' });
      // 只有换了区才写。同区重复击键/失焦(每多敲一个字符、每次 change 都会进来)原来也整份
      // persist —— 在一个开了很久的旧标签页里,这等于把旧选择(常常还没有时段)盖回服务器。
      // 2026-09-28 实测复现:旧标签页重敲一遍同一邮编 → 时段被清空、surcharge 归 0、下午档
      // $14.95 行留在车里,与订单 TPS6991TO 一模一样。同区时服务器上本来就是这个邮编,无需再写。
      if (changed) {
        this.persist();
        // 换区 = 时段已清空 → 附加费行必须跟着撤,不能只清属性不撤钱
        this.reconcileFees();
      }
      this.render();
    }
    selectDate(dateISO) {
      if (this.state.date === dateISO) {
        // 点的是已选中的日期:若换轨面板开着,同样视为「放弃换轨」→ 灭面板出确认句
        // (店主四轮补:toggle 场景里已选日就是回归目标,早退不能把面板留在原地)
        if (this._swapFor) { this._swapFor = null; this._swapBackNote = true; this.render(); } // 全量重画:时间段步随面板解锁
        return;
      }
      this.state.date = dateISO;
      // PDP 意向日期:只有**手动**选日期才让意向作废(提示一并撤下)。自动带入那次(经
      // applyPdpIntent 进来,_applyingPdpIntent 在真)必须留着意向 —— 换方式/换邮编会清空
      // 已选日期,意向还在才能在新方式下复核后自动补回(店主实测反馈 2026-08-17:
      // pickup 带入成功后切 delivery+邮编,日期不该就此丢掉)。
      if (this._pdpIntentApplied && this._pdpIntentApplied !== dateISO) this._pdpIntentApplied = null;
      this._pdpIntentFail = null;
      if (!this._applyingPdpIntent) { try { if (window.TPSlots && window.TPSlots.intent) window.TPSlots.intent.clear(); } catch (e) {} }
      // date drives which windows are valid → reset the time choice
      this.state.windowId = this.state.windowLabel = this.state.windowKind = null; this.state.surcharge = '0';
      // 店主四轮(2026-08-26):选中了「本来就可订」的日期 = 客人放弃换轨 → 面板立即消失,
      // 换成一句「已回到 Express/advance 日期」确认,绝不继续挂着误导
      if (this._swapFor) { this._swapFor = null; this._swapBackNote = true; }
      track('delivery_date_selected', { tp_date: dateISO, tp_dow: dowOf(parseYMD(dateISO)) });
      this.persist(); this.reconcileFees(); this.render();
    }
    selectWindow(id, label, kind, surcharge) {
      this.state.windowId = id; this.state.windowLabel = label; this.state.windowKind = kind;
      this.state.surcharge = surcharge || '0';
      track('delivery_window_selected', { tp_window: label });
      if (this.state.surcharge !== '0') track('surcharge_added', { tp_amount: this.state.surcharge });
      this.persist(); this.reconcileFees(); this.render();
    }

    // ── fee lines (§8.2 midday surcharge + §8.1 base-delivery compensation) ─────────
    // Two independent hidden line items keyed by properties._tp_fee ('midday' | 'base'):
    //   midday → +$9.95/+$14.95 midday courier surcharge (plus_5/plus_8 zones)
    //   base   → +$4.95 to re-collect native Local Delivery's base fee when the midday line
    //            pushes the cake subtotal over the free-over-$85 threshold (native would then
    //            waive it at checkout). Both are excluded from the cake subtotal by the snippet.
    desiredFeeLines() {
      var s = this.state, cov = s.coverage, V = this.data.FEE_PRODUCT_VARIANTS, out = {};
      // no cake (all deleted) or a mixed/blocked cart → no fee lines (cascade-clear orphans)
      var speed = this.speedClass();
      if (speed === 'empty' || speed === 'mixed') return out;
      var midday = s.method === 'delivery' && s.windowKind === 'midday' && cov && cov.surchargeVariant;
      if (midday) out.midday = cov.surchargeVariant;
      // door-to-door premium service (§ owner 2026-07-08): all delivery zones, optional
      if (s.method === 'delivery' && s.door && cov && cov.deliverable && V.door) out.door = this.doorVariantFor(cov);
      if (midday && V.base_delivery && cov.feeZone) {
        var z = this.data.FEE_ZONES[cov.feeZone];
        var cake = this.cartSubtotalCents(); // cake only — snippet excludes fee lines
        var sur = Math.round(parseFloat(s.surcharge || '0') * 100);
        // only compensate when the cart clears $85 with the discount buffer — never risk a double-charge
        if (z && cake < z.freeAt && cake + sur >= z.freeAt / (1 - DISCOUNT_BUFFER)) out.base = V.base_delivery;
      }
      return out;
    }
    variantNumericId(gid) { var m = String(gid).match(/(\d+)$/); return m ? m[1] : gid; }

    // Which door-to-door variant applies right now — see DOOR_BASE_CENTS above for the why.
    // Band = the cart WOULD have been charged the base delivery fee, but adding $9.95 tips it
    // over this zone's free-delivery threshold. Judged on the BASE price, never the raised one.
    doorVariantFor(cov) {
      var V = this.data.FEE_PRODUCT_VARIANTS;
      if (!V.door_high) return V.door; // high-tier product not configured yet → always base price
      var z = cov && cov.feeZone ? this.data.FEE_ZONES[cov.feeZone] : null;
      if (!z || !z.freeAt) return V.door;
      var sub = this.cartSubtotalCents(); // cakes + add-ons, excludes every fee line
      if (sub <= 0) return V.door;
      return (sub < z.freeAt && sub + DOOR_BASE_CENTS >= z.freeAt) ? V.door_high : V.door;
    }
    doorPriceCents() {
      var V = this.data.FEE_PRODUCT_VARIANTS;
      return (V.door_high && this.doorVariantFor(this.state.coverage) === V.door_high) ? DOOR_HIGH_CENTS : DOOR_BASE_CENTS;
    }
    currentFeeLines() {
      var empty = { midday: [], base: [], door: [] };
      return fetch(routeFor('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          // Every matching line, not just the last one. The old shape (`res[k] = {...}`) silently
          // overwrote duplicates, so a cart that HAD two door lines looked like it had one — the
          // reconciler could neither see the problem nor fix it.
          var res = { midday: [], base: [], door: [] };
          (cart.items || []).forEach(function (it) {
            var k = it.properties && it.properties._tp_fee;
            if (res[k]) res[k].push({ key: it.key, num: String(it.variant_id), qty: it.quantity });
          });
          return res;
        }).catch(function () { return empty; });
    }

    // single-flight, mirroring persist() (fixed 2026-08-23 after an order lost its delivery date).
    // Two reconciles in flight both read the same "before" cart, both decide to swap, and both
    // add — that is a duplicate fee line. Concurrent callers now share the running promise, and a
    // request that arrives mid-flight re-runs once at the end against fresh cart state.
    reconcileFees() {
      var self = this;
      if (this._feeInFlight) { this._feeAgain = true; return this._feeInFlight; }
      var p = this.reconcileFeesOnce()
        .catch(function (err) { if (window.console && console.warn) console.warn('[tp-fee] reconcile failed', err); })
        .then(function () {
          self._feeInFlight = null;
          if (self._feeAgain) { self._feeAgain = false; return self.reconcileFees(); }
          // 整条队列排空后,若真的加/删过费用行 → 重画一次购物车的商品行+总价(见 redrawCartLines)
          if (self._feeChanged) { self._feeChanged = false; return self.redrawCartLines(); }
        });
      this._feeInFlight = p;
      return p;
    }
    reconcileFeesOnce() {
      var self = this, desired = this.desiredFeeLines();
      return this.currentFeeLines().then(function (existing) {
        var chain = Promise.resolve();
        ['midday', 'base', 'door'].forEach(function (kind) {
          var want = desired[kind] ? self.variantNumericId(desired[kind]) : null;
          var have = (existing[kind] || []).slice();
          // ① Self-heal: collapse any pre-existing duplicates down to one line before deciding.
          if (have.length > 1) {
            have.slice(1).forEach(function (dup) { chain = chain.then(function () { return self.removeFee(dup.key); }); });
            have = have.slice(0, 1);
          }
          var cur = have[0] || null;
          if (want && !cur) chain = chain.then(function () { return self.addFee(kind, want); });
          else if (!want && cur) chain = chain.then(function () { return self.removeFee(cur.key); });
          else if (want && cur && want !== cur.num) {
            // ② Swap variants: remove FIRST, and let a failed removal abort the add. Adding while
            //    the old line is still there is exactly how you end up with two door-to-door fees.
            chain = chain.then(function () { return self.removeFee(cur.key).then(function () { return self.addFee(kind, want); }); });
          } else if (want && cur && cur.qty > 1) {
            chain = chain.then(function () { return self.setFeeQty(cur.key, 1); }); // qty tampered → back to 1
          }
        });
        return chain;
      }).then(function () { return self.collapseDuplicateFees(); });
    }
    // ③ Final convergence pass. Whatever went wrong above (network, race, stale key), the cart must
    //    never be left showing two of the same fee. Removal-only, so it can never make things worse.
    collapseDuplicateFees() {
      var self = this;
      return this.currentFeeLines().then(function (f) {
        var chain = Promise.resolve(), extra = 0;
        ['midday', 'base', 'door'].forEach(function (k) {
          (f[k] || []).slice(1).forEach(function (dup) { extra++; chain = chain.then(function () { return self.removeFee(dup.key); }); });
        });
        return extra ? chain : null;
      }).catch(function () {});
    }
    addFee(kind, num) { this._feeChanged = true; return cartWrite(routeFor('cart_add_url', '/cart/add.js'), { items: [{ id: num, quantity: 1, properties: { _tp_fee: kind } }] }); }
    removeFee(key) { this._feeChanged = true; return cartWrite(routeFor('cart_change_url', '/cart/change.js'), { id: key, quantity: 0 }); }
    setFeeQty(key, qty) { this._feeChanged = true; return cartWrite(routeFor('cart_change_url', '/cart/change.js'), { id: key, quantity: qty }); }

    // ── 费用行变了 → 重画购物车的商品行与总价(店主 2026-09-28 实测报障)─────────────
    // 费用行(下午档/送上楼/基础运费补收)是这里用 Ajax 直接加减的,主题自己的购物车界面只在它
    // 自己的按钮改车时才重画。结果:关掉「送上楼」后服务器上那一行已经删了,可 /cart 页和抽屉里
    // 那一行和总价还挂着,看起来像没取消;反过来打开时也不出现。
    // 只换「商品行 + 总价」这两块,stepper 本身绝不重建(客人正在操作它,整块换掉会丢焦点/展开态,
    // 结账闸门也挂在它身上)。商品区里客人可能正在写的留言(.tp-order-note)和摘要栏的配送镜像
    // ([data-tp-cart-delivery],初始化时缓存了节点)原样搬进新树,不重建。
    // 失败/找不到节点 → 什么都不做(纯显示问题,绝不影响购物车数据和结账)。
    redrawCartLines() {
      var self = this, seq = (this._redrawSeq = (this._redrawSeq || 0) + 1), root, sid, parts;
      if (!this.isConnected) return Promise.resolve(); // 已被抽屉重建换下的旧实例不许动页面
      if (this.context === 'cart') {
        root = this.closest('cart-items-component'); sid = root && root.dataset.sectionId;
        parts = ['#main-cart-products', '.cart-total'];
      } else {
        root = document.querySelector('cart-drawer'); sid = 'cart-drawer';
        parts = ['#CartDrawer-CartItems', '.cart-drawer__footer'];
      }
      if (!root || !sid) return Promise.resolve();
      var url = (this.context === 'cart' ? location.pathname : routeFor('root_url', '/')) + '?sections=' + encodeURIComponent(sid) + ',cart-icon-bubble';
      // 不带 Accept: application/json —— 在 /cart 上带了它,Shopify 回的是购物车 JSON 而不是 sections
      return fetch(url)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (json) {
          if (!json || !json[sid] || seq !== self._redrawSeq || !self.isConnected) return; // 更新的一次已在路上 → 让它来画
          var doc = new DOMParser().parseFromString(json[sid], 'text/html');
          parts.forEach(function (sel) {
            var cur = root.querySelector(sel), next = doc.querySelector(sel);
            if (!cur || !next || cur.contains(self)) return;
            ['.tp-order-note', '[data-tp-cart-delivery]'].forEach(function (keep) {
              var live = cur.querySelector(keep), fresh = next.querySelector(keep);
              if (live && fresh) fresh.replaceWith(live);
            });
            cur.replaceWith(next);
          });
          var drawer = document.querySelector('cart-drawer');
          if (json['cart-icon-bubble'] && drawer && typeof drawer.updateCartIconBubble === 'function') {
            try { drawer.updateCartIconBubble({ sections: { 'cart-icon-bubble': json['cart-icon-bubble'] } }); } catch (e) {}
          }
          // /cart 的结账表单(form#cart)在商品区里,刚被换成新节点 → 拦截器重新挂上
          if (!self.form || !self.form.isConnected) {
            self.form = self.ownerForm();
            if (self.form && !self.form._tpBound) { self.form._tpBound = true; self.form.addEventListener('submit', function (e) { self.onCheckoutSubmit(e); }, true); }
          }
          paintSlotRecap();
        })
        .catch(function () {});
    }

    checkCapacity(dateISO, windowId) {
      if (!L2_PROXY_ENABLED) return Promise.resolve(true);
      return fetch(L2_PROXY_PATH + '?date=' + encodeURIComponent(dateISO) + '&window=' + encodeURIComponent(windowId), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : { available: true }; }).then(function (j) { return j.available !== false; }).catch(function () { return true; });
    }

    // 🔴 竞态修复 2026-08-23(事故:订单 TPS6653TO 的服务器购物车丢了 date/window,只剩
    // method/postal/surcharge,而闸门照常放行)。原实现每次调用**直接发一个并发 POST**,
    // 且 `this.syncing` 被后来者覆盖 —— method/postal/date/window 四步的写入可以同时在
    // 网络里飞。最紧的触发点在同一个同步 tick 内:checkPostal() 先 persist()(此刻
    // state.date 仍为 null → 写空日期)→ 紧接着 render() → renderDate() → applyPdpIntent()
    // → selectDate() → 再 persist()(带日期)。两个 POST 相隔不到 1ms 发出,同一 HTTP/2
    // 连接多路复用**没有完成顺序保证**;空日期那个后完成 → 服务器 date/window 被清空,
    // 而本地 state 仍显示完整 → isComplete() 为真 → 结账放行 → 订单缺配送日期。
    //
    // 改为 single-flight + 合并最新:任何时刻最多一个写在飞,期间到来的意图合并进
    // _pending(只保留最新),前一个完成后再发。三个后果:
    //   ① 服务器永远不可能被旧快照覆盖(顺序由队列保证,不靠网络);
    //   ② 邮编每击键一触发的连发自动收敛 → 请求变少不是变多;
    //   ③ `this.syncing` 语义从"最后登记的那一个"变成"整条队列排空",闸门等它才是真等完。
    // 写失败不再被静默吞掉(_writeFailed 留痕),但仍然**绝不抛出**——分析/持久化永远不能
    // 打断结账。
    // 本地选择 → 5 个购物车属性(persist 写入、结账前核对共用同一份,绝不两处各拼各的)
    attrSnapshot() {
      var a = {};
      a[ATTR.method] = this.state.method || '';
      a[ATTR.postal] = this.state.method === 'delivery' ? (this.state.postal || '') : '';
      a[ATTR.date] = this.state.date || '';
      a[ATTR.window] = this.state.windowLabel || '';
      a[ATTR.surcharge] = this.state.surcharge || '0';
      return a;
    }
    persist() {
      var self = this, a = this.attrSnapshot();
      this._pending = a;                                   // 总是记下最新意图
      if (this._writing) return this.syncing;              // 已有写在飞 → 不开第二条,合并等它
      var pump = function () {
        var body = self._pending;
        self._pending = null;
        self._writing = true;
        // single source of truth = cart attributes. After a successful write, tell any sibling
        // instance (drawer + /cart coexist on the cart page) to re-hydrate from /cart.js so the two
        // never diverge. Whoever writes broadcasts; everyone re-reads — no instance-to-instance calls.
        // C02 (2026-09-08):走 cartWrite 而不是 jsonFetch —— Shopify 对失败的属性写入回 4xx+JSON
        // 错误体,jsonFetch 不看状态码会把它当成功(_writeFailed=false),闸门随后放行,订单缺日期。
        // cartWrite 在非 2xx 上 reject → 落到下面的 catch 留痕。仍然绝不向外抛。
        return withTimeout(PERSIST_TIMEOUT_MS, function (signal) { return cartWrite(routeFor('cart_update_url', '/cart/update.js'), { attributes: body }, signal); })
          .then(function () {
            self._writeFailed = false;
            document.dispatchEvent(new CustomEvent('tp:delivery-updated', { detail: { source: self } }));
            xtabAnnounce(); // 同一浏览器的其他页面 → 从购物车重读(见文件上方「跨标签页同步」)
          })
          .catch(function () { self._writeFailed = true; })
          .then(function () {
            self._writing = false;
            return self._pending ? pump() : null;          // 期间有新意图 → 接着发最新的
          });
      };
      this.syncing = pump();
      return this.syncing;
    }

    // ── checkout flush (C02, 2026-09-08;店主 2026-09-09 定为 fail-open) ─────────────
    // 离开去 /checkout 之前尽最大努力把资料送到服务器:①等属性队列排空 ②等费用行 reconcile
    // 收尾 ③若最后一次属性写入失败(HTTP 错误或断网),用最新 state 再写一次 ④再等一次费用行。
    // 结果 { ok, reason },**永不 reject**;整体 8 秒上限,挂死的请求不能把结账冻住。
    // 🔴 ok=false 时**照样放行**(店主 2026-09-09:「有问题也先让它结账,先做成转化,缺的信息
    // 事后补」)。调用方只负责记一条 tp_checkout_unverified(reason=save_failed|timeout),
    // 让我们知道有多少单是带着可能不全的资料去付款的;绝不拦客人。
    flushForCheckout() {
      var self = this, LIMIT_MS = 8000, t0 = Date.now();
      var settle = function (p) { return (p && typeof p.then === 'function') ? p.then(function () {}, function () {}) : Promise.resolve(); };
      var timeout = new Promise(function (resolve) { setTimeout(function () { resolve('timeout'); }, LIMIT_MS); });
      var work = settle(this.syncing)
        .then(function () { return settle(self._feeInFlight); })
        .then(function () { return self._writeFailed ? settle(self.persist()) : null; })
        .then(function () { return settle(self._feeInFlight); })
        // 2026-09-28:回读核对。「写入成功」≠「服务器上就是客人选的」—— 订单 TPS6991TO 放行时
        // 一切正常,落单却没有时段、只剩下午档 $14.95 行。离开前从 /cart.js 读回来逐项比对,
        // 对不上就用当前选择再写一次、把费用行按当前选择再对一遍;仍对不上 → 记 save_failed。
        .then(function () { return settle(self.verifyServerCopy()); })
        // 2026-09-20:原生配送偏好同步放在**最后一步** —— 属性和费用行都落地之后、导航之前,
        // 此刻读到的一定是客人的最新选择。三条刻意的设计:
        //   ① 它只花「剩余预算减 1 秒」,外层那个 8 秒 race 永远还留着至少 1 秒余量 →
        //      新机制不可能把 reason='timeout' 的原有含义(预约属性没救回来)搞混;
        //   ② 它 never reject,且结果**不参与**下面 ok/reason 的判定 —— ok 仍然只表示
        //      「预约属性是否保存成功」,既有的 tp_checkout_unverified 信号一个字没变;
        //   ③ 它失败只记自己的 tp_pref_sync_failed,所以四个结账入口一行都不用改。
        .then(function () { return settle(self.syncDeliveryPreference(LIMIT_MS - 1000 - (Date.now() - t0))); })
        .then(function () { return self._writeFailed ? 'save_failed' : 'ok'; });
      return Promise.race([work, timeout]).then(function (r) { return r; }, function () { return 'save_failed'; })
        .then(function (r) {
          if (r === 'ok') return { ok: true, reason: null };
          // 2026-09-29(TPS7012TO):核对没跑完(超时/写失败)时,放行前最后读一次服务器。
          // 服务器上**确实缺**方式/日期/时段 → 不放行:页面同步成服务器上的最新状态,带客人回到
          // 缺的那一步(多点一下即可)。读不到服务器(网络问题)→ 照 2026-09-09 口径放行。
          // 拦下时返回一个永不 settle 的 Promise:四个结账入口都只在它 resolve 后才跳转,
          // 于是一处都不用改(layout 内联闸门也不用动),客人再点一次结账会走正常闸门。
          return self.serverMissingStep().then(function (miss) {
            if (!miss) return { ok: false, reason: r };
            self.blockForServerGap(miss);
            return new Promise(function () {});
          });
        });
    }

    // 服务器购物车上缺哪一步('method' | 'date' | 'window'),齐全或读不到 → null。永不 reject。
    // 邮编不在此列:订单有真实收货地址,缺它不影响排产,不值得为它拦客人。
    serverMissingStep() {
      return withTimeout(FINAL_READ_TIMEOUT_MS, function (signal) {
        return fetch(routeFor('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' }, signal: signal })
          .then(function (res) { return res.ok ? res.json() : null; });
      }).then(function (cart) {
        if (!cart || !cart.attributes) return null;
        var a = cart.attributes;
        if (!a[ATTR.method]) return 'method';
        if (!a[ATTR.date]) return 'date';
        if (!a[ATTR.window]) return 'window';
        return null;
      }).catch(function () { return null; });
    }
    blockForServerGap(miss) {
      var self = this;
      trackRefusal('checkout', 'server_missing_' + miss, this.state.method);
      try { document.dispatchEvent(new CustomEvent('tp:need-delivery')); } catch (e) {}
      this.hydrateFromCart().then(function () {
        try { self.gotoStep(self.firstMissingStep() || miss, true); } catch (e) {}
      });
    }

    // 结账前回读核对(见 flushForCheckout)。永不 reject;只在确实对不上时才多发请求。
    // 属性:服务器缺失/为空 与 本地空串 视为相同(Shopify 写空串 = 删掉该属性)。
    // 费用行:无论属性是否一致都按当前选择再 reconcile 一次 —— 它先读车,已一致则零写入。
    verifyServerCopy() {
      var self = this;
      var readDiff = function () {
        return fetch(routeFor('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var want = self.attrSnapshot(), have = cart.attributes || {};
            return Object.keys(want).filter(function (k) { return String(have[k] || '') !== String(want[k] || '') && !(k === ATTR.surcharge && !have[k] && want[k] === '0'); });
          });
      };
      return readDiff().then(function (diff) {
        var fix = diff.length ? self.persist().then(readDiff) : Promise.resolve([]);
        return fix.then(function (left) {
          if (diff.length) track('tp_checkout_attr_repaired', { funnel_stage: 'checkout', detail: diff.join(','), reason: left.length ? 'still_mismatch' : 'repaired' });
          if (left.length) self._writeFailed = true;
          return self.reconcileFees();
        });
      }).catch(function () {});
    }

    // ── 原生配送偏好同步(2026-09-20;设计说明见文件上方 PREF_* 一段)─────────────
    // 当前购物车身份:每次现取,绝不缓存、绝不写死、绝不从别的标签页或 localStorage 盲取。
    // 走本地化 cart_url,和 persist()/reconcileFees() 读的是同一辆车。
    // 拿不到 / 空车 / 格式不认识 → 返回 null,调用方停止写入(绝不建备用车、绝不换一辆车)。
    cartIdentity() {
      return fetch(routeFor('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart || !cart.token || !cart.item_count) return null;
          return cartGidFrom(cart.token);
        })
        .catch(function () { return null; });
    }

    // 把「客人此刻选的方式」写进原生偏好。**永不 reject**,返回 { ok, reason }。
    // 串行单飞:每辆车只允许一条写入链(沿用 persist() 里已验证过的思路),所以一个旧的
    // PICK_UP 请求不可能在新的 SHIPPING 之后完成并把它覆盖掉 —— 顺序由队列保证,不靠网络。
    syncDeliveryPreference(budgetMs) {
      var self = this;
      if (!prefSyncEnabled()) return Promise.resolve({ ok: false, reason: 'disabled' });
      if (!PREF_ENUM[this.state.method || '']) return Promise.resolve({ ok: false, reason: 'no_method' });
      var budget = Math.min(PREF_BUDGET_MS, budgetMs == null ? PREF_BUDGET_MS : budgetMs);
      if (!(budget > 0)) return Promise.resolve({ ok: false, reason: 'no_budget' });

      var run = function () {
        return self.cartIdentity().then(function (gid) {
          if (!gid) return { ok: false, reason: 'identity_failed' };
          // 排队等待期间客人可能又改了方式 —— 这里**重新**取一次最新值,绝不用入队时的快照。
          var m = PREF_ENUM[self.state.method || ''];
          if (!m) return { ok: false, reason: 'no_method' };
          var ctl = (typeof AbortController === 'function') ? new AbortController() : null;
          var timer = null;
          var expire = new Promise(function (resolve) {
            timer = setTimeout(function () {
              if (ctl) { try { ctl.abort(); } catch (err) {} }
              // ⚠️ 到时只代表「我们不等了」,不代表服务端没写成功。结果不明一律记为未确认,
              // 绝不在这里盲发一个相反的偏好去「纠正」—— 那才是真会造成乱序的操作。
              resolve({ ok: false, reason: 'timeout' });
            }, budget);
          });
          var req = writeDeliveryPreference(gid, m, ctl ? ctl.signal : undefined).then(
            function () { return { ok: true, reason: null }; },
            function (err) { return { ok: false, reason: (err && err.message) || 'sync_failed' }; }
          );
          return Promise.race([req, expire]).then(function (res) {
            if (timer) { clearTimeout(timer); timer = null; }
            return res;
          });
        });
      };

      // 串成一条链:前一次无论成败都不会阻断后一次,但两次绝不并发。
      var p = (this._prefChain || Promise.resolve()).then(run, run)
        .catch(function () { return { ok: false, reason: 'sync_failed' }; });
      this._prefChain = p.then(function () {}, function () {});
      return p.then(function (res) { self.notePrefResult(res); return res; });
    }

    // 机制静默失效必须看得见(否则它哪天坏了没人知道)。每页一条,永不抛错。
    // disabled / no_method 不是故障,不报。契约同 docs/specs/C4-event-contract-v1.md。
    notePrefResult(res) {
      try {
        if (!res || res.ok) return;
        if (res.reason === 'disabled' || res.reason === 'no_method') return;
        if (this._prefReported) return;
        this._prefReported = true;
        track('tp_pref_sync_failed', {
          funnel_stage: 'checkout',
          reason: res.reason || 'sync_failed',
          method: this.state.method || 'none'
        });
      } catch (err) { /* 埋点永远不能影响结账 */ }
    }

    // ── completeness ───────────────────────────────────────────────────────────────
    isMixed() { return this.speedClass() === 'mixed'; }
    // An outer-zone postal code under that zone's minimum is NOT ready: native checkout offers no
    // Local Delivery there (verified 2026-09-24), so letting it through would strand the customer.
    methodReady() { var s = this.state; return s.method === 'pickup' || (s.method === 'delivery' && s.coverage && s.coverage.deliverable && !this.belowZoneMin()); }
    isComplete() { return !this.isMixed() && this.methodReady() && !!this.state.date && !!this.state.windowLabel; }
    firstMissingStep() {
      var s = this.state;
      if (!s.method) return 'method';
      if (s.method === 'delivery' && !(s.coverage && s.coverage.deliverable && !this.belowZoneMin())) return 'postal';
      if (!s.date) return 'date';
      if (!s.windowLabel) return 'window';
      return null;
    }
    onCheckoutSubmit(e) {
      // on the /cart desktop modal the stepper is hidden until opened — surface it before highlighting
      // C4: the two lines below are the checkout gate. Instrumentation is bolted
      // on beside them — no condition, no return, no ordering is touched. How
      // often checkout is refused, and for which of these five reasons, is the
      // direct answer to where add-to-cart → checkout leaks.
      if (this.isMixed()) { trackRefusal('checkout', 'mixed_cart', this.state.method); e.preventDefault(); e.stopImmediatePropagation(); document.dispatchEvent(new CustomEvent('tp:need-delivery')); this.flagMixed(); return; }
      if (!this.isComplete()) { trackRefusal('checkout', 'missing_' + (this.firstMissingStep() || 'unknown'), this.state.method); e.preventDefault(); e.stopImmediatePropagation(); document.dispatchEvent(new CustomEvent('tp:need-delivery')); this.gotoStep(this.firstMissingStep(), true); return; }
      if (this._checkoutReady) return;
      e.preventDefault(); e.stopImmediatePropagation();
      var self = this, form = this.form;
      // resubmit WITH the checkout submitter: a bare requestSubmit() drops the button's
      // name=checkout from the form data, so POST /cart just updates the cart and never
      // reaches checkout (found 2026-07-08 e2e).
      var submitter = e.submitter || form.querySelector('button[name="checkout"], input[name="checkout"]') || document.querySelector('button[name="checkout"][form="' + form.id + '"]');
      track('checkout_clicked', { tp_method: this.state.method });
      // Do NOT fire begin_checkout here: the official Google & YouTube app emits the reserved
      // begin_checkout when the real checkout loads; a second one double-counts the funnel.
      // C02 (2026-09-08):不再只等 syncing —— 等属性+费用行都落地,写入失败自动重试一次;
      // 仍失败照样放行(店主 2026-09-09 fail-open),只记 tp_checkout_unverified 供事后核单。
      this.flushForCheckout().then(function (r) {
        if (!r.ok) track('tp_checkout_unverified', { funnel_stage: 'checkout', reason: r.reason || 'save_failed', method: self.state.method || 'none' });
        self._checkoutReady = true;
        try { if (form.requestSubmit && submitter) form.requestSubmit(submitter); else if (form.requestSubmit) form.requestSubmit(); else form.submit(); }
        catch (err) { form.submit(); }
      });
    }

    revalidate() {
      var self = this;
      // self-heal the checkout binding (2026-07-18): on an EMPTY cart the drawer renders no
      // CartDrawer-Form, so connectedCallback bound nothing; when the form appears after an
      // AJAX morph this re-binds. The document-level fallback guard below still covers any gap.
      if (!this.form || !this.form.isConnected) {
        this.form = this.ownerForm();
        if (this.form && !this.form._tpBound) { this.form._tpBound = true; this.form.addEventListener('submit', function (e) { self.onCheckoutSubmit(e); }, true); }
      }
      // refresh the line list first (mixed/all_express verdicts must see the LIVE cart, not the SSR snapshot)
      this.refreshLines().then(function () {
        if (self.state.date && !self.dateAvailability(parseYMD(self.state.date)).ok) {
          self.state.date = null;
          self.state.windowId = self.state.windowLabel = self.state.windowKind = null; // kind too — a stale 'midday' would keep the fee line alive
          self.state.surcharge = '0';
          self.persist();
        } else if (self.state.date && self.state.windowLabel) {
          // the cart still carried an old label (e.g. "5:30–11:30 PM" / "6:30–11:00 PM" before 2026-10-12):
          // write the current one back so the order shows what the page shows.
          if (self._legacyWindow) { self._legacyWindow = false; self.persist(); }
          // window-level revalidation: a "tomorrow midday" picked before the 5:00 PM cut-off
          // must not survive past it — the date stays valid (evening still runs) but the
          // window must be re-chosen; reconcileFees() below then drops the stale fee line.
          var offered = self.windowsForDate(self.state.date);
          var stillOffered = offered.some(function (w) { return w.label === self.state.windowLabel; });
          if (!stillOffered) {
            self.state.windowId = self.state.windowLabel = self.state.windowKind = null;
            self.state.surcharge = '0';
            self.persist();
          }
        }
        // cart items may have changed (cake subtotal ↕) → the base-delivery compensation may need
        // adding/removing; keep both fee lines correct after any cart mutation.
        self.reconcileFees();
        self.render();
      });
    }

    // re-read in-memory state from the live cart attributes (single source of truth). Fired when a
    // sibling instance writes (tp:delivery-updated) so the drawer and /cart stepper stay in lockstep.
    hydrateFromCart() {
      var self = this;
      return fetch(routeFor('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var a = cart.attributes || {};
          self.state.method = a[ATTR.method] || null;
          self.state.postal = a[ATTR.postal] || null;
          self.state.coverage = self.state.postal ? self.data.lookupFSA(self.state.postal) : null;
          self.state.date = a[ATTR.date] || null;
          self.state.windowLabel = self.normWindow(a[ATTR.window]);
          self.state.windowKind = self.inferWindowKind();
          self.state.windowId = null;
          self.state.surcharge = a[ATTR.surcharge] || '0';
          if (self.els.postalInput) self.els.postalInput.value = self.state.postal || '';
          return self.currentFeeLines().then(function (f) { self.state.door = f.door.length > 0; self.render(); });
        }).catch(function () {});
    }
    // old evening labels from carts written before a label change → current label (TPDeliveryData)
    normWindow(label) {
      var d = this.data || window.TPDeliveryData;
      var out = (d && d.normalizeWindowLabel) ? d.normalizeWindowLabel(label) : (label || null);
      if (label && out !== label) this._legacyWindow = true;
      return out;
    }
    // derive the window kind from a persisted window label (used on load + cross-instance hydrate)
    inferWindowKind() {
      var w = this.state.windowLabel, W = this.data.WINDOWS;
      if (!w) return null;
      if (w === W.evening.label) return 'evening';
      if (w === W.midday.label) return 'midday';
      if (w === W.special.label || (W.special_stouffville && w === W.special_stouffville.label)) return 'special';
      return 'pickup';
    }

    // ── DATE availability (§8.4) — independent of the time choice ────────────────────
    // asSpeed(可选):按假想的车速重算 —— 快赢1 用 'all_express' 模拟「整车换成 Express 后
    // 这一天能不能订」,从而把 cutoff/周日/产能等其余规则原样保留。
    dateAvailability(ms, asSpeed) {
      var s = this.state, data = this.data, speed = asSpeed || this.speedClass();
      // `code` is internal-only: `reason` is rendered as the day button's title, so an
      // internal enum must never go in there. Without `code` these three paths all
      // collapsed to `unspecified` in analytics (codex source review, 2026-08-15).
      if (speed === 'mixed' || speed === 'empty') return { ok: false, code: speed === 'mixed' ? 'mixed_cart' : 'empty_cart' };
      var np = this.nowParts();
      var todayMs = parseYMD(np.date);
      var i = Math.round((ms - todayMs) / 86400000);
      if (i < 0 || i > DATE_HORIZON_DAYS) return { ok: false, code: 'out_of_horizon' };
      // 手填产能状态(shop metafield ops.slot_status → window.TPSlotManual):pct:0 = 订满、
      // closed = 休假 —— 两种状态两种文案,绝不混用。查表是同步的(SSR 注入,零请求)。
      // 放在最前:订满/休假与商品池、邮编、截单无关,谁也覆盖不了它。数据缺失 → 返回 null → 零影响。
      var tpSlot = window.TPSlots && window.TPSlots.blocked(fmtYMD(ms));
      if (tpSlot) return { ok: false, reason: tpSlot };
      var allExpress = speed === 'all_express';
      if (allExpress) { if (i > 1) return { ok: false, reason: 'Express items deliver today or next-day only' }; }
      else { if (i < 2) return { ok: false, reason: 'Advance items start the day after tomorrow' }; }

      var method = s.method, cov = s.coverage, dow = dowOf(ms);
      // special-zone weekday limits
      if (method === 'delivery' && cov && cov.kind === 'special' && cov.days && cov.days.indexOf(dow) === -1) return { ok: false, reason: 'Not a delivery day for this area' };
      // same-day gating
      if (i === 0) {
        // wants today, but the cart isn't all-express — commercially the most
        // interesting refusal on this path, and it was invisible before
        if (!allExpress) return { ok: false, code: 'sameday_not_express' };
        // no same-day service at all on listed weekdays (Sunday) — owner order 2026-07-19
        if ((data.NO_SAMEDAY_DOW || []).indexOf(dowIdx(ms)) !== -1) return { ok: false, reason: 'No same-day service on Sundays — next-day is available' };
        var cutoff = method === 'pickup' ? data.CUTOFFS.pickup : data.CUTOFFS.delivery;
        if (np.time >= cutoff) return { ok: false, reason: 'Same-day cut-off (' + this.cutoffLabel(cutoff) + ') has passed' };
      }
      // next-day rollover (owner 2026-07-23): past 17:00 TOMORROW is treated like today, so a
      // day with no same-day service at all (Sunday) closes entirely — delivery AND pickup.
      // i.e. Sunday orders close at 5:00 PM Saturday; before that, Sunday books normally.
      if (i === 1 && np.time >= data.CUTOFFS.middayNextDay && (data.NO_SAMEDAY_DOW || []).indexOf(dowIdx(ms)) !== -1) return { ok: false, reason: 'Orders for Sunday close at 5:00 PM Saturday' };
      // a pickup date with zero remaining slots is unbookable whichever day it is —
      // e.g. today late in the day, or Sunday (1–2 PM only) as "tomorrow" past the 17:00
      // rollover, whose 3–4 PM floor leaves nothing (see pickupSlots)
      if (method === 'pickup' && !this.pickupSlots(ms).length) return { ok: false, reason: i === 0 ? 'No pickup times left today' : 'No pickup times available for this date' };
      // 同理:店主把这天的配送档位全关了 → 这个日期选了也没时段可挑,直接置灰,别让客人走进死胡同
      if (method === 'delivery' && cov && cov.deliverable && !this.windowsForDate(fmtYMD(ms)).length) return { ok: false, reason: 'No delivery times available for this date' };
      return { ok: true };
    }
    cutoffLabel(hhmm) { var p = hhmm.split(':'), hh = +p[0]; return (((hh + 11) % 12) + 1) + ':' + p[1] + ' ' + (hh >= 12 ? 'PM' : 'AM'); }

    // ── TIME options for the chosen date (§8.2/§8.4/§8.5) ─────────────────────────────
    // 店主按天关掉的档位(配置台「档期状态」→ metafield ops.slot_status 的 off 数组)。
    // 与上面那些自动规则(备货时长、17:00 次日翻滚、周日不接当天)是两回事:那些是系统算的,
    // 这个是店主手动关的 —— 例如 2–3 PM 老被抢光、想把订单推到 4–6 PM。
    // 拿不到数据 → 空数组 → 什么都不关(渐进增强,绝不因为缺数据把档位全砍掉)。
    ownerClosedSlots(ms) {
      try {
        return (window.TPSlots && window.TPSlots.offFor) ? window.TPSlots.offFor(fmtYMD(ms)) : [];
      } catch (e) { return []; }
    }
    pickupSlots(ms) {
      var np = this.nowParts(), data = this.data;
      var dayIdx = Math.round((ms - parseYMD(np.date)) / 86400000);
      var list = (dowIdx(ms) === 0) ? data.PICKUP_WINDOWS.sunday : data.PICKUP_WINDOWS.weekday;
      // Earliest slot (owner rule 2026-07-23, see PICKUP_SAMEDAY in tp-delivery-data.js):
      //   today    → max(3–4 PM floor, order hour + 2h)  (8 AM→3–4, 2:xx→4–5, 3:xx→5–6)
      //   tomorrow → past the 17:00 rollover it behaves like today at the floor (3–4 PM);
      //              before 17:00 all slots stay open (2–3 PM included)
      //   later    → all slots
      var ps = data.PICKUP_SAMEDAY, minHour = null;
      if (dayIdx === 0) minHour = Math.max(ps.floorHour, parseInt(np.time, 10) + ps.prepHours);
      else if (dayIdx === 1 && np.time >= data.CUTOFFS.middayNextDay) minHour = ps.floorHour;
      var off = this.ownerClosedSlots(ms);
      return list.filter(function (w) { return minHour == null || parseInt(w.start, 10) >= minHour; })
        .filter(function (w) { return off.indexOf(w.id) < 0; })
        .map(function (w) { return { id: w.id, label: w.label, kind: 'pickup', surcharge: '0' }; });
    }
    windowsForDate(dateISO) {
      var s = this.state, data = this.data, ms = parseYMD(dateISO);
      if (s.method === 'pickup') return this.pickupSlots(ms);
      if (!(s.coverage && s.coverage.deliverable)) return [];
      var cov = s.coverage;
      var off = this.ownerClosedSlots(ms);   // 店主按天关掉的配送档位
      if (cov.kind === 'special') {
        return (off.indexOf('special') >= 0) ? []
          : [{ id: 'special', label: (cov.windows && cov.windows[0] && cov.windows[0].label) || data.WINDOWS.special.label, kind: 'special', surcharge: '0' }];
      }
      var out = [];
      if (off.indexOf('evening') < 0) out.push({ id: 'evening', label: data.WINDOWS.evening.label, kind: 'evening', surcharge: '0' });
      // midday: next-day+ only, tier plus_5/plus_8 needs a fee variant (free tier always ok).
      // Next-day itself closes at the 5:00 PM cut-off (owner rule 2026-07-20) — past 17:00
      // tomorrow is evening-only; the day after and beyond are unaffected.
      var np = this.nowParts();
      var dayIdx = Math.round((ms - parseYMD(np.date)) / 86400000);
      var middayOpen = dayIdx >= 1 && !(dayIdx === 1 && np.time >= data.CUTOFFS.middayNextDay);
      var middayUsable = middayOpen && off.indexOf('midday') < 0
        && cov.middayTier !== 'not_available' && (cov.middayTier === 'free' || cov.surchargeVariant);
      if (middayUsable) out.push({ id: 'midday', label: data.WINDOWS.midday.label, kind: 'midday', surcharge: String(cov.surchargeCharge || 0) });
      return out;
    }

    // ── render ───────────────────────────────────────────────────────────────────────
    render() {
      var s = this.state, mixed = this.isMixed();
      this.els.mixedNotice.hidden = !mixed;
      if (mixed) this.renderMixed();
      this.els.steps.hidden = mixed;
      // 混池车:客人得先解决混池,产能状态在这里是噪音 → 整块清掉
      if (mixed) { var sb = this.querySelector('[data-tp-slot-status]'); if (sb) { sb.textContent = ''; sb.hidden = true; } this.updateCtaHint(); return; }

      this.markMethod();
      this.els.postal.hidden = s.method !== 'delivery';
      if (s.method === 'delivery') this.renderPostalResult(s.coverage);
      var ready = this.methodReady();

      // DATE (calendar) — always visible; locked until method/postal ready
      this.els.date.hidden = false;
      if (!s.method) { this._swapDaysAvail = false; this.setLocked(this.els.date, this.els.dateBox, 'Choose how you’ll get it first'); }
      else if (s.method === 'delivery' && !ready) { this._swapDaysAvail = false; this.setLocked(this.els.date, this.els.dateBox, this.postalLockMsg()); }
      else { this.unlock(this.els.date); this.renderDate(); }

      // TIME — always visible; locked until a date is picked
      // 店主七轮(2026-08-26):换轨面板开着时时间段必须锁住 —— 此刻 state.date 还是旧日期,
      // 时间列表若照常显示,客人会以为点了换轨日就已经选完了(其实还没 confirm)
      this.els.window.hidden = false;
      if (!ready || !s.date) this.setLocked(this.els.window, this.els.windowOptions, 'Choose a date first');
      else if (this._swapFor) this.setLocked(this.els.window, this.els.windowOptions, 'Confirm the switch above — or pick another date — to choose a time');
      else { this.unlock(this.els.window); this.renderWindows(); }

      // PREMIUM services — delivery only, optional (door-to-door; owner 2026-07-08)
      if (this.els.premium) {
        this.els.premium.hidden = s.method !== 'delivery';
        if (s.method === 'delivery') {
          if (!ready) this.setLocked(this.els.premium, this.els.premiumBox, this.postalLockMsg());
          else { this.unlock(this.els.premium); this.renderPremium(); }
        }
      }

      this.renderGuidance();
      this.renderMinNotice();
      this.renderDeliveryNote(!!this.desiredFeeLines().base);
      // 展示点⑤:完成态状态药丸 + 选中红/深红日期的内联警示条(挂载点在 tp-delivery-stepper.liquid)。
      // 每次 render 都重算,双实例经 tp:delivery-updated → hydrateFromCart() → render() 自动同步。
      if (window.TPSlots) window.TPSlots.renderCartStatus(this);
      // 换轨面板开着时,旧日期的产能状态条(「Availability for Sat, Aug 29…」)一并藏起,
      // 别让客人把它读成新日期的状态
      if (this._swapFor) { var ssEl = this.querySelector('[data-tp-slot-status]'); if (ssEl) ssEl.hidden = true; }
      this.updateCtaHint();
    }
    // fable5's UX guard: when we pre-collect the base delivery fee (because the midday line will
    // make native Local Delivery show Free), tell the customer why checkout will read "Free".
    renderDeliveryNote(active) {
      var n = this.querySelector('[data-tp-delivery-note]'); if (!n) return;
      n.textContent = active ? 'Delivery shows as Free at checkout — your delivery fee is already collected above.' : '';
      n.hidden = !active;
    }
    setLocked(step, box, msg) { step.classList.add('is-locked'); replace(box, [h('p', { class: 'tp-locked' }, [msg])]); }
    unlock(step) { step.classList.remove('is-locked'); }

    markMethod() {
      var m = this.state.method;
      this.querySelectorAll('[data-tp-method]').forEach(function (b) { var on = b.dataset.tpMethod === m; b.setAttribute('aria-pressed', on ? 'true' : 'false'); b.classList.toggle('is-selected', on); });
    }

    renderPostalResult(cov) {
      var el = this.els.postalResult; if (!el) return;
      if (this.state.postal && this.els.postalInput && !this.els.postalInput.value) this.els.postalInput.value = this.state.postal;
      if (!cov) { el.hidden = true; return; }
      el.hidden = false;
      // no zone/area name here — the FSA→name mapping has wrong labels (owner report 2026-07-19,
      // L6C showed "Mississauga / Brampton / Oakville"); a plain yes + fee preview is all we promise
      // outer zone under its minimum = no delivery at checkout → say so plainly, with the way out
      var low = cov.deliverable ? this.belowZoneMin() : null;
      if (low) {
        el.className = 'tp-postal-result is-error';
        replace(el, ['Delivery to your area starts at ', strong(dollars(low.minOrder)), ' — add ' + money(low.minOrder - this.cartSubtotalCents()) + ' more to unlock it, or choose store pickup. Every full-size cake qualifies on its own. ', this.miniLink('zone_block')]);
        this.trackMinNotice('zone_block');
        return;
      }
      if (cov.deliverable) { el.className = 'tp-postal-result is-success'; replace(el, ['✓ We deliver to your address'].concat(this.feePreviewNodes(cov)).concat([h('span', { class: 'tp-muted' }, [' · premium services may add a surcharge'])])); }
      else { el.className = 'tp-postal-result is-error'; replace(el, ["Sorry, we don’t deliver to this postal code — choose store pickup or contact us."]); }
    }
    // ── $30 standard delivery minimum (owner 2026-09-24) ─────────────────────────
    // The minimum = the core zone's full-rate band edge (FEE_ZONES.Z1 first tier) — the same number
    // the outer zones use as minOrder. Subtotal = cakes + add-ons, fee lines excluded (same rule as
    // every other fee preview here). A fee line can only push Shopify's own subtotal UP, so this
    // side never promises a rate the checkout won't honour.
    minOrderCents() { var z = this.data.FEE_ZONES && this.data.FEE_ZONES.Z1; return (z && z.tiers && z.tiers[0] && z.tiers[0].under) || 0; }
    // delivery + a covered postal code whose zone won't deliver below its minimum → that fee result
    belowZoneMin() {
      var s = this.state, cov = s.coverage;
      if (s.method !== 'delivery' || !cov || !cov.deliverable) return null;
      var f = this.data.computeBaseFee(cov.feeZone, this.cartSubtotalCents());
      return f && f.belowMin ? f : null;
    }
    postalLockMsg() {
      var low = this.belowZoneMin();
      return low ? 'Add ' + money(low.minOrder - this.cartSubtotalCents()) + ' more to unlock delivery to your area' : 'Enter a deliverable postal code first';
    }
    miniLink(variant) {
      var a = h('a', { class: 'tp-guidance__link', href: COLL_MINI }, ['Add a mini cake ›']);
      a.addEventListener('click', function () { if (window.TPTrack && window.TPTrack.event) window.TPTrack.event('tp_min_order_click', { variant: variant }); });
      return a;
    }
    trackMinNotice(variant) {
      // once per page view per variant — render() runs on every cart/state change
      try { if (window.TPTrack && window.TPTrack.once) window.TPTrack.once('tp_min_order_' + variant, 'tp_min_order_notice', { variant: variant }); } catch (e) {}
    }
    // Reminder card under the postal step. Shown while the cart is under $30 and the customer hasn't
    // chosen pickup. Hidden for out-of-area codes (moot) and for outer zones under their minimum —
    // there the postal result itself says "no delivery under $30" (owner: don't show both).
    renderMinNotice() {
      var n = this.els.minNotice; if (!n) return;
      var s = this.state, cov = s.coverage, min = this.minOrderCents(), sub = this.cartSubtotalCents();
      var show = min > 0 && sub > 0 && sub < min && s.method !== 'pickup';
      if (show && s.method === 'delivery' && cov && (!cov.deliverable || this.belowZoneMin())) show = false;
      if (!show) { n.textContent = ''; n.hidden = true; return; }
      var gap = money(min - sub), nodes, variant;
      var f = (s.method === 'delivery' && cov && cov.deliverable) ? this.data.computeBaseFee(cov.feeZone, sub) : null;
      var at = f ? this.data.computeBaseFee(cov.feeZone, min) : null;
      if (f && f.fee != null && at && at.fee != null && at.fee < f.fee) {
        // core zone, postal known: real numbers — what they pay now vs. at $30
        variant = 'core';
        nodes = [
          h('strong', { class: 'tp-min-notice__title' }, ['Add ' + gap + ' more to bring delivery down to ' + money(at.fee)]),
          h('span', { class: 'tp-min-notice__body' }, [dollars(min) + ' is our standard delivery minimum. Below it, delivery is charged at the full rate of ' + money(f.fee) + ', with no reduced or free delivery. Every full-size cake qualifies on its own. ', this.miniLink(variant)]),
        ];
      } else {
        // no postal code yet (or method not chosen)
        variant = 'general';
        nodes = [
          h('strong', { class: 'tp-min-notice__title' }, ['Add ' + gap + ' more to reach our ' + dollars(min) + ' delivery minimum']),
          h('span', { class: 'tp-min-notice__body' }, [dollars(min) + ' is our standard delivery minimum. Below it, delivery is charged at the full rate, and some outer areas can’t be delivered to. Every full-size cake qualifies on its own. ', this.miniLink(variant)]),
        ];
      }
      replace(n, nodes); n.hidden = false;
      this.trackMinNotice(variant);
    }
    feePreviewNodes(cov) {
      var sub = this.cartSubtotalCents(), f = this.data.computeBaseFee(cov.feeZone, sub); if (!f) return [];
      if (f.belowMin) return [' · add ' + money(f.minOrder - sub) + ' to reach the ' + money(f.minOrder) + ' minimum'];
      if (f.free) return [' · ', strong('free delivery')];
      return [' · from ' + money(f.fee) + ' ', h('span', { class: 'tp-muted' }, ['(free over ' + money(f.freeAt) + ')'])];
    }

    // ── PDP 意向日期(sessionStorage,tp-slots-manual.js 的 PDP 日历写入) ─────────
    // PDP 上点的日子只是「意向」:能不能订要等方式/邮编定了才知道,所以在日期步第一次可交互时
    // 用 dateAvailability() 复核 —— 过 = 走正常 selectDate 自动选中(持久化/埋点不多不少),
    // 不过 = 把原因摆出来让客人改选。绝不绕过任何现有校验,绝不多发事件。
    applyPdpIntent() {
      if (this.state.date) return false;
      var I = window.TPSlots && window.TPSlots.intent;
      if (!I) return false;
      var iso = I.get();
      if (!iso) { this._pdpIntentFail = null; return false; }
      var av = this.dateAvailability(parseYMD(iso));
      if (!av.ok) {
        // 有客人能读的原因才展示;code-only(空车/混池等)静默。意向留着不清 ——
        // 原因可能出在方式/邮编上,客人换个方式再进日期步时这里会重试。
        this._pdpIntentFail = av.reason ? { iso: iso, reason: av.reason } : null;
        return false;
      }
      // 带入成功也**不清**意向(sessionStorage 交给 selectDate 的手动分支去清):换方式/换邮编
      // 会清空已选日期,意向还在才能在新方式下复核后自动补回(店主实测反馈 2026-08-17)。
      this._pdpIntentFail = null;
      this._pdpIntentApplied = iso;
      this.calMonth = ymOf(parseYMD(iso)); // 日历直接翻到那个月,别让客人自己找
      this._applyingPdpIntent = true;
      try { this.selectDate(iso); } finally { this._applyingPdpIntent = false; } // 触发 persist + render;重入时 state.date 已定,不会再进这里
      return true;
    }

    // calendar month grid
    renderDate() {
      // 意向日期在日期步第一次可交互时消费;应用成功 = selectDate 已同步重画整个日历,本轮就此打住
      if (this.applyPdpIntent()) return;
      var box = this.els.dateBox, np = this.nowParts(), todayMs = parseYMD(np.date);
      // default the visible month to the first available date's month
      if (!this.calMonth) {
        var firstMs = todayMs, guard = 0;
        while (guard++ <= DATE_HORIZON_DAYS && !this.dateAvailability(firstMs).ok) firstMs = addDays(firstMs, 1);
        this.calMonth = ymOf(firstMs);
      }
      var monthMs = this.calMonth, first = new Date(monthMs), y = first.getUTCFullYear(), mo = first.getUTCMonth();
      var lead = first.getUTCDay(), daysInMonth = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
      var minMonth = ymOf(todayMs), maxMonth = ymOf(addDays(todayMs, DATE_HORIZON_DAYS));
      var self = this;

      var header = h('div', { class: 'tp-cal__head' }, [
        h('button', { type: 'button', class: 'tp-cal__nav', 'data-tp-cal-nav': '-1', 'aria-label': 'Previous month', disabled: monthMs <= minMonth ? '' : null }, ['‹']),
        h('span', { class: 'tp-cal__title' }, [MON[mo] + ' ' + y]),
        h('button', { type: 'button', class: 'tp-cal__nav', 'data-tp-cal-nav': '1', 'aria-label': 'Next month', disabled: monthMs >= maxMonth ? '' : null }, ['›']),
      ]);
      var dowRow = h('div', { class: 'tp-cal__dow' }, DOW_MIN.map(function (d) { return h('span', null, [d]); }));
      // 快赢1+反向(店主批 2026-08-26 两轮):被商品池挡住的日期不再死灰。
      //   advance 车 → 今/明两天可点,换 Express 双胞胎(可能问口味/尺寸);
      //   express 车 → 后天起的日期可点,换 advance 版拿预订折扣(精确同变体,不再多问)。
      // 只有「换完这天真的可订」才亮(cutoff/周日/产能/特区路线日用 asSpeed 模拟重验)。
      var swapDays = {}, swapDir = null, sc = this.speedClass();
      if (sc === 'all_advance' || sc === 'all_express') {
        swapDir = sc === 'all_advance' ? 'express' : 'advance';
        var simSpeed = swapDir === 'express' ? 'all_express' : 'all_advance';
        var sPlan = this.planSwap(swapDir, this._swapChoice);
        if (sPlan.moves.length || sPlan.chooses.length) {
          var dayCands = [];
          if (swapDir === 'express') { dayCands = [todayMs, addDays(todayMs, 1)]; }
          else { for (var dc = 1; dc <= daysInMonth; dc++) dayCands.push(Date.UTC(y, mo, dc)); }
          dayCands.forEach(function (sMs) {
            if (!self.dateAvailability(sMs).ok && self.dateAvailability(sMs, simSpeed).ok) swapDays[fmtYMD(sMs)] = sPlan;
          });
        }
      }
      this._swapDir = swapDir;
      // 八轮(店主 2026-08-26):日历上有可点的换轨日时,底部那段「Express items are for…」
      // 引导文案就是重复噪音 → renderGuidance 据此隐藏;换不了轨(无配对/特区死角)才显示
      this._swapDaysAvail = Object.keys(swapDays).length > 0;
      var swapTitle = swapDir === 'advance'
        ? 'Advance-booking date — tap to switch to the advance version (pre-order discount applies)'
        : 'Available with the Express version — tap to switch';
      var grid = h('div', { class: 'tp-cal__grid' }, []);
      for (var b = 0; b < lead; b++) grid.appendChild(h('span', { class: 'tp-cal__pad' }, []));
      for (var day = 1; day <= daysInMonth; day++) {
        var ms = Date.UTC(y, mo, day), iso = fmtYMD(ms), av = self.dateAvailability(ms);
        var isToday = iso === np.date, sel = iso === self.state.date;
        var swap = !av.ok && !!swapDays[iso];
        var cls = 'tp-cal__day' + (sel ? ' is-selected' : '') + (isToday ? ' is-today' : '') + (av.ok ? '' : (swap ? ' is-swap' : ' is-off'));
        var dayBtn = h('button', { type: 'button', class: cls, 'data-tp-day': iso, 'data-tp-swap': swap ? '1' : null, disabled: (av.ok || swap) ? null : '', title: av.ok ? null : (swap ? swapTitle : (av.reason || 'Unavailable')), 'aria-pressed': sel ? 'true' : 'false' }, [String(day)]);
        // 展示点①:手填产能状态的色点 + tooltip(有数据才挂,无数据零变化)
        if (window.TPSlots) window.TPSlots.decorateDay(dayBtn, iso, av.ok);
        grid.appendChild(dayBtn);
      }
      // PDP 意向日期的两句回话:带入成功(✓ 已选中)/ 带入失败(说清原因,请改选)
      var intentNote = null;
      if (this._pdpIntentApplied && this.state.date === this._pdpIntentApplied) {
        intentNote = h('p', { class: 'tp-cal__intent is-ok' }, ['✓ ', strong(niceDay(this.state.date)), ' — your date from the product page.' + (this.state.windowLabel ? '' : ' Pick a time below.')]);
      } else if (!this.state.date && this._pdpIntentFail) {
        intentNote = h('p', { class: 'tp-cal__intent is-warn' }, [strong(niceDay(this._pdpIntentFail.iso)), ' — your date from the product page isn’t available here: ', this._pdpIntentFail.reason, '. Please pick another date.']);
      }
      // 快赢1:换轨确认面板(点了换轨日才出现)+ 换完后的一次性确认条(sessionStorage 过桥,
      // 撑过 cart:refresh 的整段 DOM 重建)。面板数据用本轮刚算的 plan,不再二次计算。
      var swapPanel = null;
      if (this._swapFor && swapDays[this._swapFor]) swapPanel = this.renderSwapPanel(this._swapFor, swapDays[this._swapFor]);
      else this._swapFor = null;
      // 店主四轮:面板被「选回可订日期」灭掉后,给一句方向正确的确认,而不是留人猜
      var backNote = null;
      if (!swapPanel && this._swapBackNote && this.state.date && !intentNote) {
        var backTxt = (sc === 'all_express'
          ? ' — that’s an Express date, your cart stays as is.'
          : ' — that works with your advance items, no switch needed.') + (this.state.windowLabel ? '' : ' Pick a time below.');
        backNote = h('p', { class: 'tp-cal__intent is-ok' }, ['✓ ', strong(niceDay(this.state.date)), backTxt]);
      } else if (swapPanel || !this.state.date) {
        this._swapBackNote = false;
      }
      // 无蛋 × Express 常驻说明:车里已是 Express 蛋糕却带着 Eggless=Yes(换轨后 / 混车改完后),
      // 只要还在车里就一直提示;换轨面板开着时由面板自己说,这里不重复
      // 尺寸超规则同理(店主 2026-10-09):Express 行尺寸被 version_size_limits 拒 → 同一条说明
      var eggNote = null;
      if (!swapPanel) {
        var expLines = this.cartLines().filter(function (l) { return l.speed === 'express'; });
        var expEgg = expLines.some(isEggless), expSizes = [];
        expLines.forEach(function (l) { var z = overSizeExpress(l); if (z && expSizes.indexOf(z) < 0) expSizes.push(z); });
        var setTo = (expEgg ? ['eggless'] : []).concat(expSizes).join(' and ');
        eggNote = shortNoticeNote('Your Express cake is set to ' + setTo + '. ', expEgg, expSizes);
      }
      // 2026-11-03(店主批,增长仓 DECISION-SPEED-01):手机没有 hover,换轨日只靠描边 + title 没人看见
      // (GA4 9/4–11/2:看到可换轨日 1,020 会话 → 点开 129,手机 15.7%)。有换轨日且面板没开时,
      // 日历正下方写一句可点的提示;面板开着由面板自己说,这里不重复。
      var swapHint = null;
      if (!swapPanel && this._swapDaysAvail) {
        if (swapDir === 'express') {
          var hintParts = ['Need it today or tomorrow? Tap '];
          Object.keys(swapDays).sort().forEach(function (d, i) { if (i) hintParts.push(' or '); hintParts.push(strong(niceDay(d))); });
          hintParts.push(' to switch to the Express version of your cake.');
          swapHint = h('p', { class: 'tp-cal__swaphint', 'data-tp-swap-hint': 'express' }, hintParts);
        } else {
          swapHint = h('p', { class: 'tp-cal__swaphint', 'data-tp-swap-hint': 'advance' }, ['Want a later date? Tap any outlined date to switch to the advance version — pre-order discount applies.']);
        }
      }
      replace(box, [this.swapDoneNote(), intentNote, header, dowRow, grid, swapHint, backNote, eggNote, swapPanel]);
      if (Object.keys(swapDays).length && !this._swapImpTracked) {
        this._swapImpTracked = 1;
        track('tp_swap_offer_impression', { funnel_stage: 'delivery', tp_days: Object.keys(swapDays).join(',') });
      }

      // C4: why can't they have it today / tomorrow?
      // Unavailable days render as `disabled` buttons, which emit no click, so a
      // customer's *attempt* on a blocked date is undetectable without changing
      // behaviour. What is both detectable and commercially loaded is the state
      // of the two soonest days at the moment the customer reaches the calendar
      // — the same-day / next-day promise is the whole express funnel.
      // Deduped, so at most two events per page load.
      try {
        [0, 1].forEach(function (offset) {
          var av = self.dateAvailability(addDays(todayMs, offset));
          if (av.ok) return;
          trackRefusal('date', av.code || refusalCode(av.reason), self.state.method);
        });
      } catch (e) { /* analytics must never break the cart */ }
    }

    renderWindows() {
      var wins = this.windowsForDate(this.state.date), cur = this.state.windowId, curLabel = this.state.windowLabel, box = this.els.windowOptions, self = this;
      if (!wins.length) { replace(box, [h('p', { class: 'tp-muted' }, ['No times available for this date.'])]); return; }
      var KIND_NAME = { evening: 'Evening delivery', midday: 'Afternoon delivery', special: 'Evening delivery' };
      var hasPaidMidday = false;
      var nodes = wins.map(function (w) {
        // match by id, or fall back to label so a hydrated/reloaded selection still highlights
        var on = w.id === cur || (cur == null && w.label === curLabel);
        var body = [h('span', { class: 'tp-time__label' }, [w.label])];
        // "Priority afternoon window" ONLY when the midday trip is paid — a free afternoon
        // window (some FSAs) keeps the plain "Afternoon delivery" label (owner 2026-07-20).
        var paid = w.surcharge && w.surcharge !== '0';
        var sub = (w.kind === 'midday' && paid) ? 'Priority afternoon window' : KIND_NAME[w.kind];
        if (sub) body.push(h('span', { class: 'tp-time__sub' }, [sub]));
        var kids = [h('span', { class: 'tp-time__body' }, body)];
        if (w.surcharge && w.surcharge !== '0') { hasPaidMidday = hasPaidMidday || w.kind === 'midday'; kids.push(h('span', { class: 'tp-time__fee' }, ['+' + money(Math.round(parseFloat(w.surcharge) * 100))])); }
        kids.push(h('span', { class: 'tp-time__check', 'aria-hidden': 'true' }, [on ? '✓' : '']));
        return h('button', { type: 'button', class: 'tp-time' + (on ? ' is-selected' : ''), 'data-tp-window': w.id, 'data-tp-window-label': w.label, 'data-tp-window-kind': w.kind, 'data-tp-surcharge': w.surcharge, 'aria-pressed': on ? 'true' : 'false' }, kids);
      });
      if (hasPaidMidday) nodes.push(this.infoRow('midday', 'What’s the priority window?', COPY.middayInfo));
      var sunday = this.sundayPickupNote();
      if (sunday) nodes.push(sunday);
      replace(box, nodes);
    }

    // Sunday pickup is the single 1:00–2:00 PM slot and the store closes right after
    // (owner 2026-07-27) — say it plainly under the slot so nobody plans to arrive later.
    sundayPickupNote() {
      var s = this.state;
      if (s.method !== 'pickup' || !s.date) return null;
      if (dowIdx(parseYMD(s.date)) !== 0) return null;
      return h('p', { class: 'tp-slot-note' }, [
        'Sundays ', strong('we close at 2:00 PM'),
        '. We’ll have your cake ready as early as we can and email you the moment it is — ',
        'and if that email doesn’t reach you, just come any time before 2:00 PM: it will be ready and waiting.',
      ]);
    }

    // collapsible ⓘ explanation under a paid option (open state survives re-renders via this._info)
    infoRow(id, label, text) {
      var open = !!this._info[id];
      return h('div', { class: 'tp-info' }, [
        h('button', { type: 'button', class: 'tp-info-btn', 'data-tp-info': id, 'aria-expanded': open ? 'true' : 'false' }, [label]),
        h('p', { class: 'tp-info-panel', hidden: open ? null : '' }, [text]),
      ]);
    }
    renderPremium() {
      var on = !!this.state.door;
      var cents = this.doorPriceCents(); // one price on screen at a time — never both tiers
      var card = h('button', { type: 'button', class: 'tp-time tp-service' + (on ? ' is-selected' : ''), 'data-tp-service': 'door', 'aria-pressed': on ? 'true' : 'false' }, [
        h('span', { class: 'tp-time__body' }, [
          h('span', { class: 'tp-time__label' }, [COPY.doorTitle]),
          h('span', { class: 'tp-time__sub' }, [COPY.doorSub]),
        ]),
        h('span', { class: 'tp-time__fee' }, ['+' + money(cents)]),
        h('span', { class: 'tp-time__check', 'aria-hidden': 'true' }, [on ? '✓' : '']),
      ]);
      // No note about the price tier (owner 2026-09-03): the service fee isn't quoted anywhere else
      // on the site, so the price simply shows what it is today. Spelling out "add $X and it drops
      // back to $9.95" only draws attention to a difference nobody would otherwise notice.
      replace(this.els.premiumBox, [card, this.infoRow('door', 'What’s included?', COPY.doorInfo)]);
    }

    // link to the browse-all grid — advance cakes are any product WITHOUT the *EXPRESS* label
    advanceLink() { return h('a', { class: 'tp-guidance__link', href: COLL_ADVANCE }, ['Shop Advance cakes ›']); }
    // link to the express grid — express cakes START WITH *EXPRESS*
    expressLink() { return h('a', { class: 'tp-guidance__link', href: COLL_EXPRESS }, ['Shop Express cakes ›']); }
    renderGuidance() {
      var g = this.els.guidance; if (!g) return;
      var speed = this.speedClass(), nodes = null;
      // 八轮:本引导只在「换不了轨」时显示(有换轨日则整段隐藏,见文末),所以文案不承诺
      // 「点日历自动换」,只给浏览出口
      if (speed === 'all_express') nodes = ['Express items are for today or next-day. Want a later date? Shop our Advance Order cakes — any cake without the *EXPRESS* label. ', this.advanceLink()];
      else if (speed === 'all_advance') nodes = ['Advance Order items start from the day after tomorrow. Want it today or tomorrow? Shop our Express cakes. ', this.expressLink()];
      // dead end: express window (today/tomorrow) ∩ a special zone's weekdays can be empty
      // (e.g. Hamilton on a Friday) — say so plainly instead of showing an all-grey calendar.
      if (speed === 'all_express' && this.state.method === 'delivery' && this.state.coverage && this.state.coverage.kind === 'special') {
        var t = parseYMD(this.nowParts().date);
        if (!this.dateAvailability(t).ok && !this.dateAvailability(addDays(t, 1)).ok) {
          nodes = ['This area only gets deliveries on ' + this.state.coverage.days.join(' / ') + ' — outside the Express today/next-day window right now. Choose Store pickup, or shop our Advance Order cakes for a later date. ', this.advanceLink()];
        }
      }
      // 八轮:日历本身已是可点的换轨入口(高亮日 + 面板),这段兜底引导只在换不了轨时才有价值
      if (this._swapDaysAvail) nodes = null;
      if (nodes) { replace(g, nodes); g.hidden = false; this.trackGuidance(speed); }
      else { g.textContent = ''; g.hidden = true; }
    }
    // ── P1-T7a · guidance 曝光/点击(2026-08-20) ─────────────────────────────
    // 此前这条 Express/Advance 逃生链路是测量盲区:阻断发生率可见(tp_fulfillment_blocked),
    // 但「出路被看见了吗、被点了吗」全黑。曝光=guidance 块可见 ≥50% 且停留 ≥1s,
    // 每页面加载 1 次;点击不去重(真实点击)。context 有限枚举:advance_blocked /
    // express_blocked;target:express_collection / advance_collection。
    trackGuidance(speed) {
      try {
        var g = this.els.guidance; if (!g) return;
        g._tpCtx = speed === 'all_express' ? 'express_blocked' : 'advance_blocked';
        if (!g._tpClickBound) {
          g._tpClickBound = 1;
          g.addEventListener('click', function (e) {
            try {
              var a = e.target.closest ? e.target.closest('.tp-guidance__link') : null; if (!a) return;
              track('tp_alt_express_click', {
                funnel_stage: 'delivery', context: g._tpCtx,
                target: a.getAttribute('href') === COLL_EXPRESS ? 'express_collection' : 'advance_collection'
              });
            } catch (err) { /* analytics must never break the cart */ }
          });
        }
        if (g._tpImpDone || g._tpIo || !('IntersectionObserver' in window)) return;
        var timer = 0;
        g._tpIo = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.intersectionRatio >= 0.5 && !g.hidden) {
              if (!timer) timer = setTimeout(function () {
                g._tpImpDone = 1;
                track('tp_alt_express_impression', { funnel_stage: 'delivery', context: g._tpCtx });
                g._tpIo.disconnect();
              }, 1000);
            } else if (timer) { clearTimeout(timer); timer = 0; }
          });
        }, { threshold: [0, 0.5] });
        g._tpIo.observe(g);
      } catch (e) { /* analytics must never break the cart */ }
    }
    renderMixed() {
      var lines = this.cartLines();
      var advance = lines.filter(function (l) { return l.speed !== 'express'; });
      var express = lines.filter(function (l) { return l.speed === 'express'; });
      var name = (advance[0] && advance[0].title) || 'one of your items';
      // 店主批 2026-07-28(两池引导 F3-1):死胡同改一键换轨——保留一个池,整组移除另一池
      // (含其 _tp_group fee/addon 行);被移除的可以之后单独下单。
      var pl = function (n) { return n === 1 ? ' item' : ' items'; };
      // 快赢2(店主批 2026-08-26):优先给「换版本」而不是「删掉一半」。方向随已选日期:
      // 已选日期在后天及以后 → 先给「全部改 advance」,否则先给「全部改 Same-Day」。
      // 换不动的方向(有行无配对/无货)不出按钮;删除按钮保留兜底。
      var planE = this.planSwap('express', this._swapChoice), planA = this.planSwap('advance', this._swapChoice);
      var fee = function (d) { return d === 0 ? '' : ' · ' + (d > 0 ? '+' : '−') + money(Math.abs(d)); };
      // Express 方向:没有换不动的行(blocks=0)就给按钮;还差口味没挑 → 按钮禁用 + 下方出口味选择器
      var ePending = planE.chooses.length > 0;
      var eOfferable = planE.blocks.length === 0 && (planE.moves.length > 0 || ePending);
      var convE = eOfferable ? h('button', { type: 'button', class: 'tp-mixed__btn', 'data-tp-convert': 'express', disabled: ePending ? '' : null, title: ePending ? 'Pick a flavour below to continue' : null },
        ['Make it all Express — switch ' + advance.length + ' Advance' + pl(advance.length) + (ePending ? '' : fee(planE.deltaCents))]) : null;
      var convA = planA.ok ? h('button', { type: 'button', class: 'tp-mixed__btn', 'data-tp-convert': 'advance' },
        ['Prefer a later date — switch ' + express.length + ' Express' + pl(express.length) + ' to advance' + fee(planA.deltaCents)]) : null;
      var chooseRows = ePending ? h('ul', { class: 'tp-swap__rows tp-swap__rows--mixed' }, this.swapRowNodes({ moves: [], chooses: planE.chooses, blocks: [] }, 'today / tomorrow', 'express')) : null;
      // 无蛋 × Express:改 Express 会带上无蛋的行 → 按钮下方先说清楚(店主 2026-10-05)
      var eggRows = convE ? planE.moves.map(function (m) { return m.line; }).concat(planE.chooses.map(function (c) { return c.line; })) : [];
      var eggNote = convE ? shortNoticeNote('If you switch to Express: ', eggRows.some(isEggless), planOverSizes(planE, function (l) { return l.cp && l.cp.vsl; })) : null;
      var wantLater = false;
      if (this.state.date) {
        var di = Math.round((parseYMD(this.state.date) - parseYMD(this.nowParts().date)) / 86400000);
        wantLater = di >= 2;
      }
      var convBtns = wantLater ? [convA, convE] : [convE, convA];
      var btns = h('div', { class: 'tp-mixed__btns' }, convBtns.concat([
        h('button', { type: 'button', class: 'tp-mixed__btn tp-mixed__btn--ghost', 'data-tp-keep': 'express' },
          ['Keep Express — remove ' + advance.length + ' Advance' + pl(advance.length)]),
        h('button', { type: 'button', class: 'tp-mixed__btn tp-mixed__btn--ghost', 'data-tp-keep': 'advance' },
          ['Keep Advance — remove ' + express.length + ' Express' + pl(express.length)]),
      ]));
      var lede = (convE || convA)
        ? ['Your cart mixes ', strong('Express'), ' (today / next-day) and ', strong('Advance Order'), ' (from the day after) items — including ', strong(name), ' — and their delivery dates don’t overlap. Switch everything to one version below (same cakes), or keep one type and remove the other. ', btns, eggNote, chooseRows]
        : ['Your cart mixes ', strong('Express'), ' (today / next-day) and ', strong('Advance Order'), ' (from the day after) items — including ', strong(name), ' — and their delivery dates don’t overlap. Keep one type below — the removed one can be ordered separately after. ', btns, chooseRows];
      replace(this.els.mixedNotice, lede);
    }
    // Remove every cart line of the pool we are NOT keeping — the main cake lines plus any
    // fee/addon lines sharing their _tp_group (fee lines are not individually removable in
    // the cart UI, same rationale as the configurator's removeGroup). Reload after: the
    // stepper is the checkout gate, so state must re-hydrate from server truth, not a patch.
    keepOnly(keep) {
      var self = this;
      var drop = this.cartLines().filter(function (l) { return keep === 'express' ? l.speed !== 'express' : l.speed === 'express'; });
      if (!drop.length || this._keeping) return;
      this._keeping = true;
      this.querySelectorAll('[data-tp-keep]').forEach(function (b) { b.disabled = true; b.textContent = 'Updating cart…'; });
      var dropKeys = {}; drop.forEach(function (l) { dropKeys[l.key] = 1; });
      fetch(routeFor('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var groups = {};
          (cart.items || []).forEach(function (it) { if (dropKeys[it.key] && it.properties && it.properties._tp_group) groups[it.properties._tp_group] = 1; });
          var updates = {};
          (cart.items || []).forEach(function (it) {
            if (dropKeys[it.key] || (it.properties && it.properties._tp_group && groups[it.properties._tp_group])) updates[it.key] = 0;
          });
          return jsonFetch(routeFor('cart_update_url', '/cart/update.js'), { updates: updates });
        })
        .then(function () {
          try { if (typeof gtag === 'function') gtag('event', 'tp_mixed_keep', { keep: keep }); } catch (e) {}
          window.location.reload();
        })
        .catch(function () {
          self._keeping = false;
          self.renderMixed();
        });
    }
    flagMixed() { var el = this.els.mixedNotice; el.classList.add('tp-flash'); this.scrollTo(el); setTimeout(function () { el.classList.remove('tp-flash'); }, 1200); }

    // ── 快赢1/2 · 一键换版本(店主批 2026-08-26;口味选择器+反向 = 店主二/三轮口径)──
    // 数据源 = SSR 每行的 cp(merch.*_counterpart 双胞胎变体表,见 snippet)。
    // 这是「软性防呆转换」,不动任何商品/集合/徽章。两个方向:
    //   advance→express(点今明):可能问口味/尺寸(下述层级);
    //   express→advance(点后天起):express 变体是 advance 的子集,精确同变体直换,
    //     不再问任何问题,只确认「换 advance 版拿预订折扣 + 日期」(店主三轮口径)。
    // 匹配层级(选项按名字分三类:Size 可放宽/Flavor 可放宽但必须客人亲选/其余如
    // Version 严格相等,keto↔regular 绝不静默互换):
    //   ① 全同签名且可售 → 直接换;
    //   ② 同口味不同尺寸 → 按英寸取最近(平手取小,店主口径「10 寸不行给 8 寸,
    //      要 10 寸联系客服」);盒装(非英寸)不做尺寸近似;
    //   ③ 同尺寸(或最近尺寸)但该口味目标版没有 → 弹出可选口味让客人挑,没挑完不放行;
    //   ④ 严格选项都对不上 → 进 blocks,整车换轨不放行(部分换=制造新混车)。
    planSwap(direction, choices) {
      var from = direction === 'express' ? 'advance' : 'express';
      var moves = [], blocks = [], chooses = [], deltaCents = 0;
      choices = choices || {};
      this.cartLines().forEach(function (l) {
        if (l.speed !== from) return; // 已在目标池的行不动
        if (!l.cp || !l.cp.v || !l.cp.v.length || !l.vid || !l.props) { blocks.push({ line: l, why: 'no_counterpart' }); return; }
        var want = sigParts(l.sig), wantInch = sizeInches(l.size), i, v;
        var push = function (variant, extra) {
          moves.push(Object.assign({ line: l, to: variant }, extra || {}));
          deltaCents += (variant[1] - l.cents) * (l.q || 1);
        };
        // ① 精确
        var exact = null;
        for (i = 0; i < l.cp.v.length; i++) if (l.cp.v[i][3] === l.sig) { exact = l.cp.v[i]; break; }
        if (exact && exact[2]) { push(exact, { fallbackSize: null }); return; }
        // 结构化候选:严格键(非 Size 非 Flavor)全等 + 可售
        var cands = [];
        for (i = 0; i < l.cp.v.length; i++) {
          v = l.cp.v[i];
          if (!v[2]) continue;
          var got = sigParts(v[3]), strictOk = true, k;
          for (k in want) { if (!isSizeKey(k) && !isFlavorKey(k) && got[k] !== want[k]) { strictOk = false; break; } }
          for (k in got) { if (!(k in want)) { strictOk = false; break; } }
          if (strictOk) cands.push({ v: v, parts: got, inch: sizeInches(v[4]) });
        }
        if (!cands.length) { blocks.push({ line: l, why: exact ? 'sold_out' : 'no_match' }); return; }
        var flavorKeys = Object.keys(want).filter(isFlavorKey);
        var sameFlavor = function (c) { return flavorKeys.every(function (k) { return c.parts[k] === want[k]; }); };
        var bySize = function (a, b) {
          var da = Math.abs(a.inch - wantInch), db = Math.abs(b.inch - wantInch);
          return da !== db ? da - db : a.inch - b.inch; // 距离优先,平手取小
        };
        // 客人已在口味选择器里挑过 → 用挑的(仍须在候选集内,防串数据)
        var chosen = choices[l.key];
        if (chosen) {
          for (i = 0; i < cands.length; i++) if (cands[i].v[0] === chosen) {
            push(cands[i].v, { fallbackSize: cands[i].v[4] !== l.size ? cands[i].v[4] : null, chosenFlavor: sameFlavor(cands[i]) ? null : flavorKeys.map(function (k) { return cands[i].parts[k]; }).join(' / ') });
            return;
          }
        }
        // ② 同口味,最近尺寸(盒装 wantInch=null → 不做尺寸近似)
        var cf = cands.filter(sameFlavor).filter(function (c) { return wantInch != null && c.inch != null; });
        if (cf.length) { cf.sort(bySize); push(cf[0].v, { fallbackSize: cf[0].v[4] }); return; }
        // ③ 口味在 Express 版没有 → 每个可选口味给一个最近尺寸的代表,交给客人挑
        if (flavorKeys.length) {
          var byFlavor = {};
          cands.forEach(function (c) {
            var fk = flavorKeys.map(function (k) { return c.parts[k]; }).join(' / ');
            (byFlavor[fk] = byFlavor[fk] || []).push(c);
          });
          var options = Object.keys(byFlavor).map(function (fk) {
            var group = byFlavor[fk];
            var sized = group.filter(function (c) { return wantInch != null && c.inch != null; });
            var best = sized.length ? sized.sort(bySize)[0] : group[0];
            // 盒装等无英寸尺寸:只接受同尺寸值的候选,避免静默换规格
            if (wantInch == null && best.v[4] !== l.size) return null;
            return { flavor: fk, v: best.v };
          }).filter(Boolean);
          if (options.length) { chooses.push({ line: l, options: options }); return; }
        }
        blocks.push({ line: l, why: 'no_match' });
      });
      return { moves: moves, blocks: blocks, chooses: chooses, deltaCents: deltaCents, ok: (moves.length > 0 || chooses.length > 0) && blocks.length === 0 && chooses.length === 0 };
    }
    // 先加后删:加失败(卖完/下架)时原行还在,购物车永远不缺蛋糕;属性(含 _tp_group)
    // 原样搬家,配置器的 fee/addon 行跟着新行继续成组,addon-guard 不会误清。
    // 🔴 防翻倍三层(2026-08-26 店主报重大 bug:抽屉里反复切换,旧行 key 过期 → 加新行
    // 成功、删旧行静默 404,每切一次购物车多一个蛋糕):
    //   ① 执行前先实拉 /cart.js,逐行核对 plan 里每个旧行 key/变体/数量与服务器一致,
    //      任何不一致 → 整单中止(stale plan),一个字节不动购物车;
    //   ② 删旧行的返回必须校验,失败 → 把刚加的行按数量退回(绝不留下双份),然后报错;
    //   ③ confirmSwap/confirmConvert 一律先 refreshLines() 再 planSwap(见下),不吃旧快照。
    executeSwap(plan) {
      var moves = plan.moves;
      return fetch(routeFor('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var byKey = {};
          (cart.items || []).forEach(function (it) { byKey[it.key] = it; });
          moves.forEach(function (m) {
            var live = byKey[m.line.key];
            if (!live || String(live.variant_id) !== String(m.line.vid) || live.quantity !== (m.line.q || 1)) {
              throw new Error('stale cart line: ' + m.line.key);
            }
          });
          var swapped = [];
          var chain = Promise.resolve();
          moves.forEach(function (m) {
            chain = chain.then(function () {
              return jsonFetch(routeFor('cart_add_url', '/cart/add.js'), { items: [{ id: m.to[0], quantity: m.line.q || 1, properties: m.line.props || {} }] });
            }).then(function (r) {
              if (r && r.status && r.status >= 400) throw new Error(r.description || 'cart add failed');
              var addedKey = r && r.items && r.items[0] && r.items[0].key;
              return jsonFetch(routeFor('cart_change_url', '/cart/change.js'), { id: m.line.key, quantity: 0 }).then(function (rr) {
                if (rr && rr.status && rr.status >= 400) {
                  // 删不掉旧行 → 把刚加的量退掉再报错,宁可没换成也绝不翻倍
                  var undo = Promise.resolve();
                  if (addedKey) {
                    undo = fetch(routeFor('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
                      .then(function (r2) { return r2.json(); })
                      .then(function (c2) {
                        var line = (c2.items || []).filter(function (it) { return it.key === addedKey; })[0];
                        if (!line) return null;
                        var back = Math.max(0, line.quantity - (m.line.q || 1));
                        return jsonFetch(routeFor('cart_change_url', '/cart/change.js'), { id: addedKey, quantity: back });
                      }).catch(function () {});
                  }
                  return undo.then(function () { throw new Error(rr.description || 'cart remove failed'); });
                }
                swapped.push({ m: m, addedKey: addedKey });
                return rr;
              });
            });
          });
          return chain.then(function () { return swapped; });
        });
    }
    // 换轨成功后把新行(含反向映射)手工喂回 _lines:抽屉的 section morph 若没重建实例,
    // SSR 数据是旧的,不这么做的话下一次反向换轨会因缺 cp 而不可用。morph 正常时,
    // 下一次 refreshLines 会以本数据为底、live 数量覆盖,无副作用。
    adoptSwappedLines(swapped, dir) {
      var self = this;
      var dropKeys = {};
      swapped.forEach(function (s) { dropKeys[s.m.line.key] = 1; if (s.addedKey) dropKeys[s.addedKey] = 1; });
      var lines = this.cartLines().filter(function (l) { return !dropKeys[l.key]; });
      swapped.forEach(function (s) {
        if (!s.addedKey) return;
        var m = s.m, l = m.line;
        lines.push({
          key: s.addedKey, title: (l.cp && l.cp.title) || l.title, speed: dir,
          vid: m.to[0], q: l.q || 1, cents: m.to[1], props: l.props || {}, sig: m.to[3], size: m.to[4],
          vsl: (l.cp && l.cp.vsl) || null,
          cp: { pid: 0, url: '', title: l.title, vsl: l.vsl || null, v: [[l.vid, l.cents, 1, l.sig, l.size]] },
        });
      });
      self._lines = lines;
    }
    openSwapPanel(iso) {
      this._swapFor = iso;
      track('tp_swap_offer_open', { funnel_stage: 'delivery', tp_date: iso });
      this.render(); // 全量重画:时间段步随面板一起锁住(店主七轮),不止重画日历
      var p = this.querySelector('[data-tp-swap-panel]'); if (p) this.scrollTo(p);
    }
    // 逐行渲染:已定的换法 / 口味选择器 / 换不动的说明。dir='express'|'advance'。
    // 措辞口径(店主三轮 2026-08-26):不写 same-day(明天的单写 same-day 会误导),
    // 一律叫 Express version / advance version;不可用要连尺寸一起写(10 inch Mango Oolong)。
    // 选择结果存 this._swapChoice[lineKey]=variantId,面板与混车通知共用。
    swapRowNodes(plan, dayLabel, dir) {
      var self = this, rows = [];
      var toName = dir === 'advance' ? 'Advance version' : 'Express version';
      var notAvail = dir === 'advance' ? 'isn’t available in the advance version' : 'isn’t available in Express';
      plan.moves.forEach(function (m) {
        var d = (m.to[1] - m.line.cents) * (m.line.q || 1);
        var price = d === 0 ? 'same price' : (d > 0 ? '+' : '−') + money(Math.abs(d));
        var notes = [];
        if (m.chosenFlavor) notes.push('flavour: ' + m.chosenFlavor);
        if (m.fallbackSize) notes.push(m.line.size + ' ' + notAvail + '; closest size is ' + m.fallbackSize + '. Need ' + m.line.size + '? Contact us.');
        rows.push(h('li', { class: 'tp-swap__row' }, [
          strong(cleanTitle(m.line.title)), ' → ' + toName + ' · ' + price,
          notes.length ? h('span', { class: 'tp-swap__note' }, [' — ' + notes.join(' · ')]) : null,
        ]));
      });
      plan.chooses.forEach(function (c) {
        var l = c.line;
        var flavorNow = (function () { var p = sigParts(l.sig), ks = Object.keys(p).filter(isFlavorKey); return ks.map(function (k) { return p[k]; }).join(' / '); })();
        // 店主口径:把当前选的尺寸+口味写全 —— 「10 inch Mango Oolong isn’t available…」
        var currentSel = ((l.size || '') + ' ' + (flavorNow || '')).trim() || l.size || flavorNow;
        var chips = c.options.map(function (o) {
          var d = (o.v[1] - l.cents) * (l.q || 1);
          var bits = [o.flavor];
          if (o.v[4] !== l.size) bits.push(o.v[4]);
          bits.push(d === 0 ? 'same price' : (d > 0 ? '+' : '−') + money(Math.abs(d)));
          var on = self._swapChoice && self._swapChoice[l.key] === o.v[0];
          return h('button', { type: 'button', class: 'tp-swap__chip' + (on ? ' is-on' : ''), 'data-tp-swap-pick': String(o.v[0]), 'data-tp-swap-line': l.key, 'aria-pressed': on ? 'true' : 'false' }, [bits.join(' · ')]);
        });
        rows.push(h('li', { class: 'tp-swap__row' }, [
          strong(cleanTitle(l.title)), ' — ', strong(currentSel), ' ' + notAvail + '. Pick an available flavour:',
          h('span', { class: 'tp-swap__chips' }, chips),
        ]));
      });
      plan.blocks.forEach(function (b) {
        var blockTxt = dir === 'advance'
          ? ' — no advance version of this configuration. Contact us and we’ll sort it out.'
          : ' — no Express version of this configuration; it needs 2+ days. Remove it to switch to ' + dayLabel + ', or contact us for urgent orders.';
        rows.push(h('li', { class: 'tp-swap__row is-blocked' }, [strong(cleanTitle(b.line.title)), blockTxt]));
      });
      return rows;
    }
    renderSwapPanel(iso, plan) {
      var dir = this._swapDir || 'express';
      var title = dir === 'advance'
        ? [strong('Want it ' + niceDay(iso) + '?'), ' That’s an advance-booking date — switch to the advance version of your cake and the ', strong('pre-order discount'), ' applies.']
        : [strong('Want it ' + niceDay(iso) + '?'), ' Switch to the Express version — same cake, available today & tomorrow.'];
      var kids = [
        h('p', { class: 'tp-swap__title' }, title),
        h('ul', { class: 'tp-swap__rows' }, this.swapRowNodes(plan, niceDay(iso), dir)),
      ];
      // 无蛋 × Express:照样能换能结账,只把说明放在确认按钮上方(店主 2026-10-05)
      // 尺寸超规则同理(店主 2026-10-09):提示放同一处,和无蛋合成一句
      if (dir === 'express' && !plan.blocks.length) {
        var swapEgg = plan.moves.map(function (m) { return m.line; }).concat(plan.chooses.map(function (c) { return c.line; })).some(isEggless);
        var swapSizes = planOverSizes(plan, function (l) { return l.cp && l.cp.vsl; });
        var note = shortNoticeNote(swapEgg && !swapSizes.length ? 'About your eggless request: ' : 'About your order: ', swapEgg, swapSizes);
        if (note) kids.push(note);
      }
      if (plan.blocks.length) {
        kids.push(h('div', { class: 'tp-mixed__btns' }, [h('button', { type: 'button', class: 'tp-mixed__btn tp-mixed__btn--ghost', 'data-tp-swap-cancel': '1' }, ['OK'])]));
      } else {
        var tot = plan.deltaCents === 0 ? '' : ' (' + (plan.deltaCents > 0 ? '+' : '−') + money(Math.abs(plan.deltaCents)) + ')';
        var pending = plan.chooses.length > 0;
        kids.push(h('div', { class: 'tp-mixed__btns' }, [
          h('button', { type: 'button', class: 'tp-mixed__btn', 'data-tp-swap-confirm': iso, disabled: pending ? '' : null, title: pending ? 'Pick a flavour above to continue' : null }, ['Switch & confirm ' + niceDay(iso) + (pending ? '' : tot)]),
          h('button', { type: 'button', class: 'tp-mixed__btn tp-mixed__btn--ghost', 'data-tp-swap-cancel': '1' },
            // 店主七轮:取消键按「他原来的日期」说话,不谈价格 —— 有已选日期就写 Keep <那天>;
            // 还没选过日期时退回中性的 Go back
            [this.state.date ? 'Keep ' + niceDay(this.state.date) : 'Go back']),
        ]));
        var fine = dir === 'advance'
          ? 'Advance pricing applies — you get the pre-order discount. Review everything in your cart before checkout.'
          : 'Express pricing applies — the pre-order discount comes off. Review everything in your cart before checkout.';
        kids.push(h('p', { class: 'tp-swap__fine' }, [pending ? 'Pick a flavour above to continue. ' : '', fine]));
      }
      if (this._swapError) { kids.push(h('p', { class: 'tp-swap__error', role: 'alert' }, [this._swapError])); this._swapError = null; }
      return h('div', { class: 'tp-swap', 'data-tp-swap-panel': '' }, kids);
    }
    confirmSwap(iso) {
      if (this._swapping) return;
      var dir = this._swapDir || 'express';
      var self = this;
      this._swapping = true;
      this.querySelectorAll('[data-tp-swap-confirm],[data-tp-swap-cancel]').forEach(function (b) { b.disabled = true; });
      var btn = this.querySelector('[data-tp-swap-confirm]'); if (btn) btn.textContent = 'Updating your cart…';
      // 🔴 防翻倍③:先从 /cart.js 刷新行(key/数量/属性),再在新鲜数据上出 plan——
      // 抽屉在 cart:refresh 没重建实例时,旧 _lines 里的 key 已经不存在,拿它去换 = 加新不删旧
      this.refreshLines().then(function () {
        var plan = self.planSwap(dir, self._swapChoice);
        if (!plan.ok) { self._swapping = false; if (!plan.chooses.length) self._swapFor = null; self.renderDate(); return null; }
        track('tp_swap_confirmed', { funnel_stage: 'delivery', direction: dir, tp_items: plan.moves.length, tp_delta_cents: plan.deltaCents, tp_date: iso });
        return self.executeSwap(plan)
          .then(function (swapped) {
            try { sessionStorage.setItem('tp_swap_done', dir); } catch (e) { /* private mode → note skipped */ }
            self._swapDoneRead = undefined; // 实例长存(抽屉不重建)时,让确认条重读最新方向
            self.adoptSwappedLines(swapped || [], dir === 'advance' ? 'advance' : 'express');
            self._swapChoice = {};  // 旧行 key 已换代,选择记录一并作废
            self.state.date = null; // 保证 selectDate 不因同值早退
            self.selectDate(iso);   // 持久化日期属性,重建后的实例直接落在选好的这天
            return self.syncing;
          })
          .then(function () {
            self._swapFor = null;
            // renderContents 成功 = 抽屉(含新 stepper)已按新 SSR 重建;_swapping 保持 true
            // 直到重画完成,挡住旧实例中途消费 tp_swap_done。重画失败才自兜底重算。
            return self.refreshCartUi().then(function (redrawn) {
              self._swapping = false;
              if (!redrawn) { self._swapDoneRead = undefined; self.revalidate(); }
            });
          })
          .catch(function () {
            self._swapping = false;
            track('tp_swap_failed', { direction: dir, tp_date: iso });
            self._swapError = 'Sorry — we couldn’t update your cart just now. Please review your cart and try again.';
            self.revalidate(); // 以服务器真相重画
          });
      });
    }
    confirmConvert(dir) {
      if (this._swapping) return;
      var self = this;
      this._swapping = true;
      this.querySelectorAll('[data-tp-convert],[data-tp-keep]').forEach(function (b) { b.disabled = true; });
      // 🔴 防翻倍③:同 confirmSwap,先刷新行再出 plan
      this.refreshLines().then(function () {
        var plan = self.planSwap(dir, self._swapChoice);
        if (!plan.ok) { self._swapping = false; self.renderMixed(); return null; }
        track('tp_swap_confirmed', { funnel_stage: 'delivery', direction: dir, tp_items: plan.moves.length, tp_delta_cents: plan.deltaCents, context: 'mixed_cart' });
        return self.executeSwap(plan)
          .then(function () {
            try { sessionStorage.setItem('tp_swap_done', dir); } catch (e) { /* private mode */ }
            window.location.reload(); // 与 keepOnly 同一收尾:混车解除后全页以服务器真相重来
          })
          .catch(function () { self._swapping = false; track('tp_swap_failed', { direction: dir, context: 'mixed_cart' }); self.revalidate(); });
      });
    }
    // 换轨完成的一次性确认(店主口径:告诉客人折扣没了、请回购物车核实)
    swapDoneNote() {
      // 换轨进行中(_swapping)或已卸下的实例不消费 sessionStorage —— confirmSwap 里的
      // selectDate 会让旧实例先重画一轮,若它读走,renderContents 重建的新实例就丢了确认条
      if (!this.isConnected || this._swapping) return null;
      if (this._swapDoneRead === undefined) {
        var v = null;
        try { v = sessionStorage.getItem('tp_swap_done'); if (v) sessionStorage.removeItem('tp_swap_done'); } catch (e) { v = null; }
        this._swapDoneRead = v || null;
      }
      if (!this._swapDoneRead) return null;
      var txt = this._swapDoneRead === 'advance'
        ? '✓ Switched to the advance versions — the pre-order discount is applied. Please review your cart items.'
        : '✓ Switched to the Express versions — Express pricing applies, so the pre-order discount is off. Please review your cart items.';
      return h('p', { class: 'tp-cal__intent is-ok' }, [txt]);
    }
    refreshCartUi() {
      // /cart 整页重载。抽屉(店主六轮反馈:换轨后商品行不刷新)——cart:refresh 其实是
      // 死事件,主题里没有任何监听者;正确做法是走主题自己的 Section Rendering 管线:
      // 拉最新 cart.js + 两个 section 的新 HTML,交给 cart-drawer.renderContents 整体重画
      // (行/图/价/小计/stepper 全部按新 SSR 重建,与加购后的原生刷新完全同路)。
      if (location.pathname.indexOf('/cart') === 0) { window.location.reload(); return Promise.resolve(true); }
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        return Promise.all([
          fetch(routeFor('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }),
          fetch(routeFor('root_url', '/') + '?sections=cart-drawer,cart-icon-bubble', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }),
        ]).then(function (res) {
          var state = res[0];
          state.sections = res[1];
          return Promise.resolve(drawer.renderContents(state)).then(function () { return true; });
        }).catch(function () { document.dispatchEvent(new CustomEvent('cart:refresh')); return false; });
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return Promise.resolve(false);
    }

    updateCtaHint() {
      var hint = this.querySelector('[data-tp-cta-hint]'); if (!hint) return;
      if (this.isMixed()) { hint.textContent = 'Please resolve the mixed-cart notice above to continue.'; hint.hidden = false; } else hint.hidden = true;
    }
    gotoStep(step, flash) {
      var map = { method: this.els.method, postal: this.els.postal, date: this.els.date, window: this.els.window };
      var el = map[step]; if (!el) return; el.hidden = false; this.scrollTo(el);
      if (flash) {
        el.classList.add('tp-flash'); setTimeout(function () { el.classList.remove('tp-flash'); }, 1200);
        var hint = this.querySelector('[data-tp-cta-hint]'); if (hint) { hint.textContent = this.missingHint(step); hint.hidden = false; }
      }
    }
    missingHint(step) {
      var low = step === 'postal' ? this.belowZoneMin() : null;
      if (low) return 'Delivery to your area starts at ' + dollars(low.minOrder) + ' — add ' + money(low.minOrder - this.cartSubtotalCents()) + ' more, or choose store pickup';
      return { method: 'Please choose delivery or pickup', postal: 'Please enter a deliverable postal code', date: 'Please select a delivery date', window: 'Please select a delivery time' }[step] || 'Please complete the delivery details';
    }
    scrollTo(el) { try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { el.scrollIntoView(); } }
    disconnectedCallback() { if (this._unsub) this._unsub(); if (this._onDelivUpdate) document.removeEventListener('tp:delivery-updated', this._onDelivUpdate); if (this._onCartUpdated) document.removeEventListener('cart:updated', this._onCartUpdated); if (this._onResume) { document.removeEventListener('visibilitychange', this._onResume); window.removeEventListener('pageshow', this._onResume); } }
  }

  customElements.define('tp-delivery-stepper', TPDeliveryStepper);

  // ── /cart delivery capture: stepper renders inline in the wide left column; the narrow summary
  // column shows a compact MIRROR of the chosen slot (desktop ≥1200px) that scrolls to the stepper.
  // No modal — the stepper is always visible where the customer is looking.
  var SUM_DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var SUM_MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function sumFmtDate(iso) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return '';
    var p = iso.split('-'), d = new Date(Date.UTC(+p[0], +p[1] - 1, +p[2]));
    return SUM_DOW[d.getUTCDay()] + ', ' + SUM_MON[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }
  function initCartSummaryMirror() {
    var wrap = document.querySelector('[data-tp-cart-delivery]');
    if (!wrap || wrap._tpInit) return;
    wrap._tpInit = true;
    var openBtn = wrap.querySelector('[data-tp-open]');

    // Mirror the live delivery selection (single source of truth = the stepper's state / the cart
    // attributes it persists), so the customer sees the chosen slot — or a "required" prompt — right
    // above Checkout. The stepper lives in the left column, so query it document-wide.
    var sum = {
      icon: wrap.querySelector('[data-tp-sum-icon]'), title: wrap.querySelector('[data-tp-sum-title]'),
      meta: wrap.querySelector('[data-tp-sum-meta]'), action: wrap.querySelector('[data-tp-sum-action]'),
    };
    function stepper() { return document.querySelector('tp-delivery-stepper'); }
    function readState() {
      var el = stepper();
      if (el && el.state) return { method: el.state.method, date: el.state.date, window: el.state.windowLabel };
      if (el) return { method: el.dataset.method || '', date: el.dataset.date || '', window: el.dataset.window || '' };
      return { method: '', date: '', window: '' };
    }
    function updateSummary() {
      if (!sum.title) return;
      var st = readState(), done = !!(st.method && st.date && st.window);
      wrap.setAttribute('data-tp-state', done ? 'done' : 'empty');
      // the icon is a CSS mask (calendar / truck / storefront) keyed off these attributes — no emoji
      wrap.setAttribute('data-tp-method', st.method || '');
      if (done) {
        sum.title.textContent = (st.method === 'pickup' ? 'Pickup' : 'Delivery') + ' · ' + sumFmtDate(st.date);
        sum.meta.textContent = st.window;
        if (sum.action) sum.action.textContent = 'Edit';
      } else {
        sum.title.textContent = 'Choose delivery date & time';
        sum.meta.textContent = 'Required before checkout';
        if (sum.action) sum.action.textContent = 'Choose';
      }
    }
    function revealStepper() {
      var el = stepper(); if (!el) return;
      // Two gotchas on /cart: (1) scrollIntoView({block:'center'}) silently no-ops on an element
      // taller than the viewport (the stepper is); (2) `behavior:'smooth'` silently fails on this
      // page because <body> has `overflow: clip`. So compute the absolute target and jump instantly.
      var top = el.getBoundingClientRect().top + window.pageYOffset - 24;
      window.scrollTo(0, top < 0 ? 0 : top);
      el.classList.add('tp-flash'); setTimeout(function () { el.classList.remove('tp-flash'); }, 1200);
    }

    if (openBtn) openBtn.addEventListener('click', revealStepper);
    // checkout blocked with details missing → bring the stepper into view (gotoStep also targets the step)
    document.addEventListener('tp:need-delivery', revealStepper);
    // the stepper broadcasts on every persist (method/date/window write) — re-read and repaint the mirror
    document.addEventListener('tp:delivery-updated', updateSummary);
    updateSummary();
    // the stepper upgrades asynchronously; repaint once its state is live so we don't stay on the SSR guess
    setTimeout(updateSummary, 0);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initCartSummaryMirror);
  else initCartSummaryMirror();

  // ── /cart SLOT RECAP (owner 2026-09-06): the chosen method/date/time restated in plain words
  // directly above the consent line + Checkout button, at EVERY width (the mirror row above is
  // ≥1200px only — on mobile the stepper has scrolled out of view by the time the customer
  // reaches the button). Same single source of truth as the mirror: the stepper's live state,
  // falling back to the SSR data-* attributes Liquid rendered from cart.attributes.
  // Deliberately NOT init-bound: the node is re-queried on every repaint, so an AJAX-morphed
  // summary column keeps updating without any re-binding step.
  function paintSlotRecap() {
    var nodes = document.querySelectorAll('[data-tp-slot-recap]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      // drawer copy lives INSIDE its own stepper; the /cart copy sits in the summary column, a
      // column away from it — so pair with the enclosing stepper first, else the page's only one.
      var st = (el.closest && el.closest('tp-delivery-stepper')) || document.querySelector('tp-delivery-stepper');
      var s = { method: '', date: '', window: '' };
      if (st && st.state) s = { method: st.state.method, date: st.state.date, window: st.state.windowLabel };
      else if (st) s = { method: st.dataset.method || '', date: st.dataset.date || '', window: st.dataset.window || '' };
      var done = !!(s.method && s.date && s.window);
      var label = el.querySelector('[data-tp-recap-label]'), value = el.querySelector('[data-tp-recap-value]');
      el.setAttribute('data-tp-state', done ? 'done' : 'empty');
      // CSS picks the matching note off this attribute — the copy itself is SSR'd by
      // snippets/tp-slot-recap.liquid and NEVER duplicated here (one place to edit wording).
      el.setAttribute('data-tp-method', done ? s.method : '');
      if (label) label.textContent = done ? (s.method === 'pickup' ? 'Pickup' : 'Delivery') : 'Delivery date & time';
      if (value) value.textContent = done ? sumFmtDate(s.date) + ' \u00b7 ' + s.window : 'Not selected yet \u2014 choose it above';
    }
  }
  // "Good to know" disclosure: collapsed by default, opens in place. Document-level and re-queried
  // per click, so it survives the drawer/summary being re-rendered by AJAX with no re-binding.
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-tp-recap-info]') : null; if (!b) return;
    var open = b.getAttribute('aria-expanded') === 'true';
    var panel = document.getElementById(b.getAttribute('aria-controls'));
    b.setAttribute('aria-expanded', open ? 'false' : 'true');
    if (panel) panel.hidden = open;
  });
  document.addEventListener('tp:delivery-updated', paintSlotRecap);
  document.addEventListener('cart:updated', function () { setTimeout(paintSlotRecap, 0); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', paintSlotRecap);
  else paintSlotRecap();
  // the custom element upgrades asynchronously — repaint once its live state exists
  setTimeout(paintSlotRecap, 0);

  // ── GLOBAL CHECKOUT GUARD — belt-and-braces (owner order 2026-07-18) ─────────────────────
  // Incident: on an empty cart the Ella drawer renders no CartDrawer-Form, so the stepper's
  // connectedCallback bound its submit interceptor to nothing; the form appearing later via
  // AJAX morph never re-ran the binding → add-to-cart → drawer → Checkout sailed through with
  // no method/date/window chosen. Rule from the owner: under NO circumstances may a checkout
  // proceed with delivery details missing — remind the customer and take them to the
  // date/time section. This document-level CAPTURE listener is that unconditional backstop:
  // it doesn't depend on any instance binding succeeding, and it routes through the stepper's
  // own onCheckoutSubmit so the scroll-to-missing-step guidance and the attribute-writing
  // resubmit flow are identical to the bound path. It steps aside when the form's own
  // listener is attached (form._tpBound) to avoid double handling.
  // Readiness that works even when the custom element failed to upgrade (script race, cached
  // old asset, partial failure — SEEN IN THE WILD 2026-07-18: the drawer's checkout sailed
  // through while `isComplete` wasn't a function yet). Upgraded instance → its live state;
  // otherwise → the SSR data-* attributes, which Liquid renders straight from cart.attributes.
  // Returns null only when the page has no stepper at all (guard stands down — no capture UI).
  function tpDeliveryReady() {
    var st = document.querySelector('tp-delivery-stepper');
    if (!st) return null;
    if (typeof st.isComplete === 'function') { try { return !!st.isComplete(); } catch (err) {} }
    var m = st.getAttribute('data-method') || '', d = st.getAttribute('data-date') || '', w = st.getAttribute('data-window') || '';
    return !!(m && d && w);
  }
  function tpBlockAndGuide(e) {
    e.preventDefault(); e.stopImmediatePropagation();
    document.dispatchEvent(new CustomEvent('tp:need-delivery'));
    var st = document.querySelector('tp-delivery-stepper');
    // C4: THIS is the gate that fires in practice. The click guard runs before any
    // submit handler (that's why it was added), so instrumenting only the
    // stepper's onCheckoutSubmit recorded nothing — measured on the preview theme,
    // 2026-08-14. Reason is read the same defensive way tpDeliveryReady() does, so
    // it still reports when the custom element never upgraded.
    try {
      var reason = 'unknown', method = 'none';
      if (st && typeof st.isMixed === 'function' && st.isMixed()) reason = 'mixed_cart';
      else if (st && typeof st.firstMissingStep === 'function') reason = 'missing_' + (st.firstMissingStep() || 'unknown');
      else if (st) {
        var m = st.getAttribute('data-method') || '', d = st.getAttribute('data-date') || '', w = st.getAttribute('data-window') || '';
        reason = !m ? 'missing_method' : (!d ? 'missing_date' : (!w ? 'missing_window' : 'unknown'));
      }
      if (st && st.state && st.state.method) method = st.state.method;
      else if (st) method = st.getAttribute('data-method') || 'none';
      trackRefusal('checkout', reason, method);
    } catch (err) { /* analytics must never break checkout */ }
    if (!st) return;
    if (typeof st.gotoStep === 'function' && typeof st.firstMissingStep === 'function') {
      try { st.gotoStep(st.firstMissingStep(), true); return; } catch (err) {}
    }
    try { st.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (err) { st.scrollIntoView(); }
  }
  // FIRST GATE — the CLICK itself. The theme's drawer JS may submit via form.submit() (the
  // method fires NO submit event) or navigate directly, so a submit-level guard alone can be
  // bypassed — verified live 2026-07-18: preventDefault on submit still ended in /checkouts/.
  // With details missing, the click on any checkout button dies here, before any theme handler.
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('button[name="checkout"], input[name="checkout"], [id="CartDrawer-Checkout"]') : null;
    if (!b) return;
    var ready = tpDeliveryReady();
    if (ready === null) return; // no capture UI on this page — stand down
    if (ready === false) { tpBlockAndGuide(e); return; }
    // Details complete → WE take the customer to checkout ourselves. Verified live 2026-07-18:
    // an app script swallows the drawer form's submit in the CAPTURE phase on an ancestor node
    // (the event never reaches the form; our probes on document and form both confirmed it),
    // so a completed click died silently — a conversion bug in itself. The delivery details are
    // already persisted to cart attributes on every selection (persist()/syncing), so no form
    // POST is needed: wait for any in-flight attribute write, then navigate to /checkout.
    e.preventDefault(); e.stopImmediatePropagation();
    var st = document.querySelector('tp-delivery-stepper');
    var go = function () {
      track('checkout_clicked', { tp_method: (st && st.state && st.state.method) || '' });
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
      window.location.href = root.replace(/\/$/, '') + '/checkout';
    };
    // C02 (2026-09-08):有 flushForCheckout 就等它(属性+费用行落地、失败自动重试一次);
    // 仍失败照样放行(店主 2026-09-09 fail-open),只记 tp_checkout_unverified。旧实例退回只等 syncing。
    if (st && typeof st.flushForCheckout === 'function') {
      st.flushForCheckout().then(function (r) {
        if (r && r.ok === false) track('tp_checkout_unverified', { funnel_stage: 'checkout', reason: r.reason || 'save_failed', method: (st.state && st.state.method) || 'none' });
        go();
      }, go);
    } else if (st && st.syncing && typeof st.syncing.then === 'function') { st.syncing.then(go, go); } else { go(); }
  }, true);
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var submitter = e.submitter || null;
    var isCheckout = (submitter && submitter.name === 'checkout') ||
      (!submitter && /\/cart\/?$/.test(form.getAttribute('action') || '') &&
        !!(form.querySelector('button[name="checkout"], input[name="checkout"]') ||
           document.querySelector('button[name="checkout"][form="' + form.id + '"]')));
    if (!isCheckout) return;
    if (form._tpBound) return; // the instance listener on this form already guards it
    var ready = tpDeliveryReady();
    if (ready === null || ready === true) return; // no capture UI on page / details complete
    var st = document.querySelector('tp-delivery-stepper');
    if (st && typeof st.onCheckoutSubmit === 'function') {
      // healthy instance: route through it — identical guidance + attribute-writing resubmit
      if (!st.form || !st.form.isConnected) st.form = form;
      st.onCheckoutSubmit(e);
      return;
    }
    tpBlockAndGuide(e); // element present but not upgraded: hard-stop, no exceptions
  }, true);
  // direct links into checkout (accelerated buttons render as forms and are disabled at the
  // theme level, but any stray /checkout anchor must obey the same rule)
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href*="/checkout"]') : null;
    if (!a) return;
    var ready = tpDeliveryReady();
    if (ready === null || ready === true) return;
    tpBlockAndGuide(e);
  }, true);
})();
